import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Target, Edit2, Trash2, Building2, AlertCircle, TrendingUp, DollarSign, Users, ArrowRight, ArrowLeft, Check, ChevronDown, Sparkles, Pencil, RefreshCw } from 'lucide-react';
import objetivosApi from '../../api/objetivos';
import adsPlatformApi from '../../api/adsPlatform';
import optimizationsApi from '../../api/optimizations';
import { useGoogleAds, useMetaAds } from '../../hooks';
import { AuthContext } from '../../components/context/AuthContext';
import { ModalsContext } from '../../components/context/ModalsContext';
import { GoogleIcon, MetaIcon } from '../../components';
import NoAdsConnectedSection from '../../components/common/NoAdsConnectedSection';
import CreateObjectivesModal from '../../components/modals/CreateObjectivesModal';
import ObjetivosFilterBar from '../../components/ObjetivosFilterBar';
import ObjectivesSetupView from '../../components/ObjectivesSetupView';
import OptimizationDetailModal from '../../components/modals/OptimizationDetailModal';
import OptimizationsListModal from '../../components/modals/OptimizationsListModal';
import { formatCurrencyAmount } from '../../utils/currency';
import { calcularProgreso, obtenerEstilosProyeccion } from '../../utils/objetivosUtils';
import {
  mapActionTypeToAction,
  mapIntentToType,
  transformItemsToDetails,
  mapPlatformToDisplay,
  groupsToSuggestions,
  suggestionToCard,
  normalizeSuggestionsResponse,
} from '../../utils/optimizationTransformers';
import { useOptimizationGenerationToast } from '../../components/context/OptimizationGenerationToastContext';
import { coalesceDescription } from '../../utils/optimizationDescription';
import usersApi, { setupSteps } from '../../api/user';
import { invalidateObjetivosQueries, useObjetivosQuery } from '../../queries/objetivos';

// Definiciones de métricas con íconos y descripciones para las cards
const METRICAS_CARDS = [
  {
    value: 'ROAS',
    label: 'ROAS',
    icon: DollarSign,
    descripcion: 'Busco maximizar el retorno.',
    color: 'from-white/5 to-white/5',
    borderColor: 'border-white/30',
    iconColor: 'text-white/70',
    hoverColor: 'hover:border-white/40 hover:from-white/10 hover:to-white/10'
  },
  {
    value: 'CPA',
    label: 'CPA',
    icon: Target,
    descripcion: 'Busco reducir el costo por acción.',
    color: 'from-white/5 to-white/5',
    borderColor: 'border-white/30',
    iconColor: 'text-white/70',
    hoverColor: 'hover:border-white/40 hover:from-white/10 hover:to-white/10'
  },
  {
    value: 'Conversiones',
    label: 'Conversiones',
    icon: Users,
    descripcion: 'Quiero más conversiones.',
    color: 'from-white/5 to-white/5',
    borderColor: 'border-white/30',
    iconColor: 'text-white/70',
    hoverColor: 'hover:border-white/40 hover:from-white/10 hover:to-white/10'
  },
  {
    value: 'Presupuesto',
    label: 'Inversión',
    icon: TrendingUp,
    descripcion: 'Controlar el gasto publicitario.',
    color: 'from-white/5 to-white/5',
    borderColor: 'border-white/30',
    iconColor: 'text-white/70',
    hoverColor: 'hover:border-white/40 hover:from-white/10 hover:to-white/10'
  }
];

// Opciones para plataformas relacionadas
const PLATAFORMAS_OPCIONES = [
  { value: 'GOOGLE', label: 'Google' },
  { value: 'META', label: 'Meta' }
];

// Funciones para formatear campañas (igual que en CampaignsPage)
const formatGoogleCampaigns = (list) =>
  (list || []).map((c) => ({
    id: c?.id || '',
    nombre: c?.name || '',
    status: c?.status === "ENABLED" ? "ACTIVE" : c?.status || '',
    plataforma: "GOOGLE",
    tipo: c?.type || 'SEARCH',
    isEsporaiCampaign: c?.is_esporai_campaign || false,
    serving_status: c?.serving_status || '',
  }));

const formatMetaCampaigns = (list) =>
  (list || []).map((c) => ({
    id: c?.id || '',
    nombre: c?.name || '',
    status: c?.status || '',
    plataforma: "META",
    tipo: c?.objective || 'TRAFFIC',
    isEsporaiCampaign: c?.is_esporai_campaign || false,
  }));

/** @param {string} plataforma */
const plataformaObjetivoToOptimizationPlatform = (plataforma) => {
  if (plataforma === 'GOOGLE') return 'google';
  if (plataforma === 'META') return 'meta';
  return null;
};

const Objetivos = () => {
  const { auth, updateContextUser } = useContext(AuthContext);
  const { openCreateObjectivesSetupModal, setIsOptimizationsListModalOpen: setGlobalOptimizationsListModalOpen, setIsOptimizationDetailModalOpen: setGlobalOptimizationDetailModalOpen } = useContext(ModalsContext);
  const { requestGeneration } = useOptimizationGenerationToast();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [refreshingMetricasActuales, setRefreshingMetricasActuales] = useState(false);
  const [error, setError] = useState(null);
  const [brandActivo, setBrandActivo] = useState(null);

  const [mostrarModal, setMostrarModal] = useState(false);
  const [editandoObjetivo, setEditandoObjetivo] = useState(null);
  const [submittingObjetivo, setSubmittingObjetivo] = useState(false);
  const [pasoActual, setPasoActual] = useState(1);
  const [presetFechas, setPresetFechas] = useState('personalizado');
  const [isCreateObjectivesAIModalOpen, setIsCreateObjectivesAIModalOpen] = useState(false);
  
  // Estados para optimizaciones
  const [activeGroupsForObjectives, setActiveGroupsForObjectives] = useState([]);
  const [loadingOptimizations, setLoadingOptimizations] = useState(false);
  const [selectedOptimization, setSelectedOptimization] = useState(null);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState(null);
  const [selectedObjectiveName, setSelectedObjectiveName] = useState(null);
  const [currentOptimizationIndex, setCurrentOptimizationIndex] = useState(0);
  const [isOptimizationModalOpen, setIsOptimizationModalOpen] = useState(false);
  const [isOptimizationsListModalOpen, setIsOptimizationsListModalOpen] = useState(false);
  
  // Estados para controlar expansión de secciones
  const [expandedSections, setExpandedSections] = useState({
    activos: true,   // Abierto por defecto
    proximos: false,
    finalizados: false
  });

  // Estados para filtros
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroPlataforma, setFiltroPlataforma] = useState('todas');
  const [filtroMetrica, setFiltroMetrica] = useState('todas');
  const [formularioObjetivo, setFormularioObjetivo] = useState({
    nombre: '',
    metrica_relacionada: '',
    plataformas_relacionadas: [],
    campanas_relacionadas: [],
    conversiones_seleccionadas: [],
    fecha_inicio: '',
    fecha_fin: '',
    target: ''
  });
  const [ultimoNombreAutogenerado, setUltimoNombreAutogenerado] = useState('');

  // Monedas por plataforma (desde /ads-platform/currencies)
  const [currenciesData, setCurrenciesData] = useState(null);
  const [, setLoadingCurrencies] = useState(false);

  // Estados para campañas
  const [_mostrarSelectorCampañas, setMostrarSelectorCampañas] = useState(false);
  const [campañasFiltradas, setCampañasFiltradas] = useState([]);
  const [filtroCampañas, setFiltroCampañas] = useState('');

  // Estados para conversiones
  const [mostrarSelectorConversiones, setMostrarSelectorConversiones] = useState(false);
  const [todasLasConversiones, setTodasLasConversiones] = useState([]);
  const [conversionesDisponibles, setConversionesDisponibles] = useState([]);
  const [conversionesSeleccionadas, setConversionesSeleccionadas] = useState([]);
  const [filtroConversiones, setFiltroConversiones] = useState('');

  // Estados para métricas
  const [_objetivoMetricas, setObjetivoMetricas] = useState({});
  const [_cargandoMetricas, setCargandoMetricas] = useState(false);


  // Hooks para Google Ads y Meta Ads
  const {
    isConnected: isGoogleConnected,
    fetchCampaigns: fetchGoogleCampaigns,
    campaigns: googleCampaigns = [],
    loadingCampaigns: loadingGoogleCampaigns,
    activeAccount: activeGoogleAccount,
    loadingActiveAccount: loadingGoogleAccount,
    connectGoogleAds,
  } = useGoogleAds();

  const {
    isConnected: isMetaConnected,
    fetchCampaigns: fetchMetaCampaigns,
    campaigns: metaCampaigns = [],
    loadingCampaigns: loadingMetaCampaigns,
    activeAccount: activeMetaAccount,
    loadingActiveAccount: loadingMetaAccount,
    startMetaOAuth,
  } = useMetaAds();

  const hasAnyConnection = isGoogleConnected || isMetaConnected;

  const completedSteps = useMemo(() => auth?.user?.setup_steps_completed || [], [auth?.user?.setup_steps_completed]);
  const isStep2Completed = completedSteps.includes(setupSteps.CREA_OBJETIVOS_AUTOMATICOS_1);

  const [selectedConversionsForSetup, setSelectedConversionsForSetup] = useState([]);
  const [selectedObjectiveMetricsForSetup, setSelectedObjectiveMetricsForSetup] = useState([]);
  const [isCreateObjectivesSetupModalOpen, setIsCreateObjectivesSetupModalOpen] = useState(false);

  const googleConversionsSetup = useMemo(() => {
    if (!isGoogleConnected || !activeGoogleAccount) return [];
    return (activeGoogleAccount.conversion_actions || [])
      ?.filter((action) => action && action.status === 'ENABLED')
      .map((action) => ({
        id: action?.id || '',
        nombre: action?.name || '',
        plataforma: 'GOOGLE',
      })) || [];
  }, [isGoogleConnected, activeGoogleAccount]);

  const metaConversionsSetup = useMemo(() => {
    if (!isMetaConnected || !activeMetaAccount) return [];
    return (activeMetaAccount.pixels?.data || [])?.flatMap((pixel) =>
      (pixel?.existing_events || [])?.map((event) => ({
        id: `${pixel?.id || ''}_${event || ''}`,
        nombre: `${pixel?.name || ''} - ${event || ''}`,
        plataforma: 'META',
      })) || []
    ) || [];
  }, [isMetaConnected, activeMetaAccount]);

  useEffect(() => {
    if (!auth?.token) return;

    let cancelled = false;

    (async () => {
      try {
        setLoadingCurrencies(true);
        const response = await adsPlatformApi.getCurrencies();
        const payload = response?.data?.data || response?.data || null;
        if (!cancelled) setCurrenciesData(payload);
      } catch (err) {
        console.error('Error cargando monedas (/ads-platform/currencies):', err);
      } finally {
        if (!cancelled) setLoadingCurrencies(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [auth?.token]);

  const currenciesByPlatform = useMemo(() => {
    return currenciesData?.by_platform || {};
  }, [currenciesData]);

  // Calcular la moneda de forma síncrona con useMemo
  const monedaDisplay = useMemo(() => {
    const googleCurrency =
      currenciesByPlatform?.google?.currency || activeGoogleAccount?.currency || null;
    const metaCurrency =
      currenciesByPlatform?.meta?.currency || activeMetaAccount?.currency || null;

    const googleConnected = currenciesByPlatform?.google?.connected ?? isGoogleConnected;
    const metaConnected = currenciesByPlatform?.meta?.connected ?? isMetaConnected;
    
    // Lógica de selección de moneda:
    // 1. Solo Google conectado: Usa la moneda de Google
    if (googleConnected && googleCurrency && !metaConnected) {
      return googleCurrency;
    }
    
    // 2. Solo Meta conectado: Usa la moneda de Meta
    if (metaConnected && metaCurrency && !googleConnected) {
      return metaCurrency;
    }
    
    // 3. Ambos conectados con la misma moneda: Usa esa moneda común
    if (googleConnected && metaConnected && googleCurrency && metaCurrency) {
      if (googleCurrency === metaCurrency) {
        return googleCurrency;
      } else {
        // 4. Ambos conectados con diferentes monedas: usar una moneda fallback (se usa moneda por plataforma cuando corresponde)
        return "";
      }
    }
    
    // 5. Ninguno conectado o no hay información: Por defecto USD
    return googleCurrency || metaCurrency || 'USD';
  }, [activeGoogleAccount, activeMetaAccount, currenciesByPlatform, isGoogleConnected, isMetaConnected]);

  const getCurrencyForPlatform = useCallback((plataformaValue) => {
    const key = plataformaValue ? plataformaValue.toLowerCase() : null;
    if (!key || (key !== 'google' && key !== 'meta')) return null;

    const fromEndpoint = currenciesByPlatform?.[key]?.currency || null;
    const fromAccount =
      key === 'google'
        ? activeGoogleAccount?.currency || null
        : activeMetaAccount?.currency || null;

    return fromEndpoint || fromAccount || null;
  }, [activeGoogleAccount, activeMetaAccount, currenciesByPlatform]);

  const getCurrencyForObjetivo = useCallback((objetivo) => {
    if (objetivo?.currency) return objetivo.currency;
    const plataforma = objetivo?.plataformas_relacionadas?.[0] || null;
    return getCurrencyForPlatform(plataforma) || monedaDisplay;
  }, [getCurrencyForPlatform, monedaDisplay]);

  // Moneda ISO a usar en el input de monto según plataforma seleccionada (Google/Meta)
  const monedaInputISO = useMemo(() => {
    const plataformaSeleccionada = formularioObjetivo?.plataformas_relacionadas?.[0] || null;
    if (!plataformaSeleccionada) return '';
    return getCurrencyForPlatform(plataformaSeleccionada) || monedaDisplay;
  }, [formularioObjetivo?.plataformas_relacionadas, getCurrencyForPlatform, monedaDisplay]);

  // Verificar si estamos esperando que las cuentas activas se carguen
  const esperandoCuentas = useMemo(() => {
    // Si Google está conectado pero aún no tenemos la cuenta activa
    const esperandoGoogle = isGoogleConnected && loadingGoogleAccount;
    // Si Meta está conectado pero aún no tenemos la cuenta activa
    const esperandoMeta = isMetaConnected && loadingMetaAccount;
    
    return esperandoGoogle || esperandoMeta;
  }, [isGoogleConnected, loadingGoogleAccount, isMetaConnected, loadingMetaAccount]);

  const brandIdForObjetivosQuery = brandActivo?.id;
  const {
    data: rawObjetivos = [],
    isPending: objetivosQueryPending,
    isError: objetivosQueryError,
    refetch: refetchObjetivos,
  } = useObjetivosQuery(brandIdForObjetivosQuery != null ? String(brandIdForObjetivosQuery) : null, {
    enabled: Boolean(brandIdForObjetivosQuery) && !esperandoCuentas,
  });

  const objetivos = useMemo(() => {
    if (!Array.isArray(rawObjetivos)) return [];
    return rawObjetivos.filter(
      (objetivo) =>
        objetivo &&
        typeof objetivo === 'object' &&
        objetivo.id &&
        objetivo.nombre !== undefined
    );
  }, [rawObjetivos]);

  const brandsLen = auth?.user?.brands?.length ?? 0;
  const loading =
    (brandsLen > 0 && !brandActivo) ||
    (Boolean(brandActivo) && (esperandoCuentas || objetivosQueryPending));

  useEffect(() => {
    if (!brandActivo || esperandoCuentas) return;
    if (objetivosQueryError) setError('Error al cargar los objetivos');
  }, [brandActivo, esperandoCuentas, objetivosQueryError]);

  // Función para obtener la marca activa del usuario
  const obtenerMarcaActiva = useCallback(() => {
    if (!auth?.user?.brands || auth.user.brands.length === 0) {
      return null;
    }

    // Buscar la marca activa por active_brand_id
    const marcaActiva = auth.user.brands.find(brand => brand.id === auth.user.active_brand_id);
    
    // Si no encuentra la marca activa, usar la primera disponible
    return marcaActiva || auth.user.brands[0];
  }, [auth?.user]);

  // Cargar marca activa cuando cambie el usuario
  useEffect(() => {
    const marcaActiva = obtenerMarcaActiva();
    setBrandActivo(marcaActiva);
    // Reset campaigns loaded flag when brand changes
    setCampaignsLoaded(false);
  }, [auth?.user, obtenerMarcaActiva]);

  // Función para transformar optimización del backend al formato del frontend
  const transformOptimization = useCallback((backendOpt) => {
    const objetivoId = backendOpt.objetivo_id;
    const objetivo = objetivos.find(obj => obj.id === objetivoId);
    const objectiveName = objetivo?.nombre || null;
    
    // Obtener métrica relacionada del objetivo para determinar target_roas o target_cpa
    let target_roas = null;
    let target_cpa = null;
    if (objetivo) {
      if (objetivo.metrica_relacionada === 'ROAS') {
        target_roas = objetivo.target;
      } else if (objetivo.metrica_relacionada === 'CPA') {
        target_cpa = objetivo.target;
      }
    }

    // Transformar items a details
    const details = transformItemsToDetails(backendOpt.items);
    
    // Determinar details_label según entity_type
    const detailsLabelMap = {
      'KEYWORD': 'Palabra Clave',
      'AD': 'Anuncio',
      'PLACEMENT': 'Ubicación',
      'OFFER': 'Producto',
      'ASSET': 'Recurso',
      'SEARCH_TERM': 'Término de Búsqueda',
      'NEGATIVE_KEYWORD': 'Palabra Clave Negativa',
      'ADSET': 'Conjunto de anuncios',
      'CAMPAIGN': 'Campaña',
    };
    const details_label = detailsLabelMap[backendOpt.entity_type] || 'Detalle';

    // Determinar si es incremento de presupuesto para daily_budget
    const normalizedActionType = (backendOpt.action_type || '').toLowerCase();
    const isBudgetAction = normalizedActionType === 'increase_budget' || 
                          normalizedActionType === 'decrease_budget';
    const daily_budget = isBudgetAction && backendOpt.value_before ? backendOpt.value_before : null;
    const new_daily_budget = isBudgetAction && backendOpt.value_after ? backendOpt.value_after : null;

    // Determinar impact_metric desde primary_kpi
    const impact_metric = backendOpt.primary_kpi || 'ROAS';

    return {
      id: backendOpt.id,
      type: mapIntentToType(backendOpt.intent),
      action: mapActionTypeToAction(backendOpt.action_type),
      platform: mapPlatformToDisplay(backendOpt.platform),
      objective_name: objectiveName || 'Objetivo',
      campaign_name: backendOpt.campaign_name || 'Campaña',
      description: coalesceDescription(backendOpt.user_text, 'Optimización sugerida'),
      impact_metric: impact_metric,
      details: details,
      details_label: details_label,
      target_roas: target_roas,
      target_cpa: target_cpa,
      daily_budget: daily_budget,
      new_daily_budget: new_daily_budget,
      expires_at: backendOpt.expires_at,
      is_liked: backendOpt.is_liked,
      // Mantener datos originales del backend por si se necesitan
      _backend: backendOpt,
    };
  }, [objetivos]);

  // Cargar optimizaciones desde el backend agrupadas por objetivo
  const loadOptimizations = useCallback(async () => {
    if (!brandActivo || objetivos.length === 0) {
      setActiveGroupsForObjectives([]);
      setLoadingOptimizations(false);
      return;
    }

    console.groupCollapsed('[Objetivos][Optimizations] loadOptimizations');
    const debugPayload = {
      brandActivo: {
        id: brandActivo?.id,
        name: brandActivo?.name,
      },
      objetivos: objetivos.map((o) => ({
        id: o?.id,
        nombre: o?.nombre,
        metrica_relacionada: o?.metrica_relacionada,
        plataformas_relacionadas: o?.plataformas_relacionadas,
        campanas_relacionadas_count: (o?.campanas_relacionadas || []).length,
      })),
    };

    setLoadingOptimizations(true);
    try {
      const response = await optimizationsApi.getOptimizations();
      const data = response?.data || {};
      const activeGroups = data?.active?.groups ?? [];
      const backendActive = groupsToSuggestions(activeGroups);
      const backendActiveSummary = backendActive.map((opt) => ({
        id: opt?.id,
        objetivo_id: opt?.objetivo_id,
        primary_kpi: opt?.primary_kpi,
        platform: opt?.platform,
        campaign_id: opt?.campaign_id,
        campaign_name: opt?.campaign_name,
      }));
      
      // Agrupar optimizaciones por objetivo_id
      const optimizationsByObjective = {};
      const assignedOptimizationIds = new Set();
      const assignmentLog = [];
      
      // Inicializar con arrays vacíos para todos los objetivos
      objetivos.forEach((objetivo) => {
        optimizationsByObjective[objetivo.id] = [];
      });
      
      // Transformar y agrupar optimizaciones
      backendActive.forEach((backendOpt) => {
        const objetivoId = backendOpt.objetivo_id;
        if (!objetivoId || !optimizationsByObjective[objetivoId]) {
          assignmentLog.push({
            opt_id: backendOpt?.id,
            objetivo_id: objetivoId,
            assigned: false,
            reason: 'missing_or_unknown_objetivo_id',
          });
          return;
        }

        const transformedOpt = transformOptimization(backendOpt);
        const optimizationId = transformedOpt.id ?? backendOpt.id;
        if (optimizationId !== null && optimizationId !== undefined) {
          if (assignedOptimizationIds.has(optimizationId)) {
            assignmentLog.push({
              opt_id: optimizationId,
              objetivo_id: objetivoId,
              assigned: false,
              reason: 'duplicate_optimization_id',
            });
            return;
          }
          assignedOptimizationIds.add(optimizationId);
        }

        if (objetivoId && optimizationsByObjective[objetivoId]) {
          optimizationsByObjective[objetivoId].push(transformedOpt);
          assignmentLog.push({
            opt_id: optimizationId,
            objetivo_id: objetivoId,
            assigned: true,
          });
        }
      });

      console.log('assignmentLog:', assignmentLog);
      const counts = Object.fromEntries(
        Object.entries(optimizationsByObjective).map(([id, items]) => [id, items.length])
      );

      console.log('[Objetivos][Optimizations] DEBUG_JSON_START');
      console.log(JSON.stringify({
        ...debugPayload,
        backendActive: backendActiveSummary,
        assignmentLog,
        optimizationsByObjective_counts: counts,
      }, null, 2));
      console.log('[Objetivos][Optimizations] DEBUG_JSON_END');
      
      setActiveGroupsForObjectives(activeGroups);
    } catch (error) {
      console.error('Error cargando optimizaciones:', error);
      // En caso de error, dejar vacío en lugar de mostrar datos mock
      const emptyOptimizations = {};
      objetivos.forEach((objetivo) => {
        emptyOptimizations[objetivo.id] = [];
      });
      setActiveGroupsForObjectives([]);
    } finally {
      setLoadingOptimizations(false);
      console.groupEnd();
    }
  }, [brandActivo, objetivos, transformOptimization]);

  // Cargar optimizaciones cuando cambien los objetivos o la marca activa
  useEffect(() => {
    if (objetivos.length > 0 && brandActivo) {
      loadOptimizations();
    } else {
      setActiveGroupsForObjectives([]);
      setLoadingOptimizations(false);
    }
  }, [objetivos, brandActivo, loadOptimizations]);

  const normalizeOptimizationTypeKey = useCallback((raw) => {
    if (raw == null) return null;
    const v = String(raw).trim().toLowerCase();
    return v || null;
  }, []);

  const PENDING_OR_SHOWN_STATUSES = useMemo(() => new Set(['NEW', 'SHOWN', 'LIKED']), []);
  const isPendingOrShownStatus = useCallback((status) => {
    if (status == null || status === '') return true; // default: consider "pendiente"
    return PENDING_OR_SHOWN_STATUSES.has(String(status).trim().toUpperCase());
  }, [PENDING_OR_SHOWN_STATUSES]);

  /**
   * Asigna cada "tipo" de optimización (rule key / action_type) a UN solo objetivo (el primero según orden en UI).
   * Esto evita contar el mismo tipo en múltiples objetivos.
   */
  const optimizationTypeKeysByObjective = useMemo(() => {
    const objectiveOrder = new Map();
    (objetivos || []).forEach((o, idx) => {
      if (o?.id != null) objectiveOrder.set(o.id, idx);
    });

    // typeKey -> { objectiveId, objectiveIdx }
    const assignment = new Map();
    const allSuggestions = groupsToSuggestions(activeGroupsForObjectives);

    allSuggestions.forEach((s) => {
      if (!isPendingOrShownStatus(s?.status)) return;
      const objectiveId = s?.objetivo_id;
      if (objectiveId == null) return;
      const objectiveIdx = objectiveOrder.has(objectiveId) ? objectiveOrder.get(objectiveId) : Number.POSITIVE_INFINITY;
      const typeKey = normalizeOptimizationTypeKey(s?.rule?.key ?? s?.action_type ?? null);
      if (!typeKey) return;

      const existing = assignment.get(typeKey);
      if (!existing || objectiveIdx < existing.objectiveIdx) {
        assignment.set(typeKey, { objectiveId, objectiveIdx });
      }
    });

    const out = {};
    assignment.forEach(({ objectiveId }, typeKey) => {
      if (!out[objectiveId]) out[objectiveId] = new Set();
      out[objectiveId].add(typeKey);
    });

    // Convert Sets to arrays for stable usage
    const normalized = {};
    Object.keys(out).forEach((k) => {
      normalized[k] = Array.from(out[k]);
    });
    return normalized;
  }, [objetivos, activeGroupsForObjectives, isPendingOrShownStatus, normalizeOptimizationTypeKey]);

  const getOptimizationTypesCountForObjective = useCallback((objetivoId) => {
    const list = optimizationTypeKeysByObjective?.[objetivoId] || [];
    return Array.isArray(list) ? list.length : 0;
  }, [optimizationTypeKeysByObjective]);

  // Grupos por objetivo para listado agrupado por regla (mismo contrato que API)
  const objectiveGroupsByObjective = useMemo(() => {
    const out = {};
    objetivos.forEach((obj) => {
      out[obj.id] = activeGroupsForObjectives
        .map((g) => ({
          ...g,
          suggestions: (g.suggestions || []).filter((s) => s.objetivo_id === obj.id),
        }))
        .filter((g) => g.suggestions.length > 0);
    });
    return out;
  }, [activeGroupsForObjectives, objetivos]);

  const getObjectiveName = useCallback(
    (id) => objetivos.find((o) => o.id === id)?.nombre ?? 'Objetivo',
    [objetivos]
  );

  // Flat list de cards (suggestionToCard) por objetivo para modal de detalle y navegación
  const getOptimizationCardsForObjective = useCallback(
    (objetivoId) => {
      const groupsForObj = objectiveGroupsByObjective[objetivoId] || [];
      const suggestions = groupsToSuggestions(groupsForObj);
      return suggestions.map((s) => suggestionToCard(s, getObjectiveName));
    },
    [objectiveGroupsByObjective, getObjectiveName]
  );

  // Manejar selección de optimización desde el listado (usa cards para índice y detalle)
  const handleSelectOptimization = useCallback((optimization) => {
    if (selectedObjectiveId) {
      const cards = getOptimizationCardsForObjective(selectedObjectiveId);
      const index = cards.findIndex((opt) => opt.id === optimization.id);
      setSelectedOptimization(optimization);
      setCurrentOptimizationIndex(index >= 0 ? index : 0);
      setGlobalOptimizationDetailModalOpen(true);
      setIsOptimizationModalOpen(true);
    }
  }, [selectedObjectiveId, getOptimizationCardsForObjective, setGlobalOptimizationDetailModalOpen]);

  // Manejar navegación entre optimizaciones
  const handleNavigateOptimization = useCallback((newIndex) => {
    if (selectedObjectiveId) {
      const cards = getOptimizationCardsForObjective(selectedObjectiveId);
      if (newIndex >= 0 && newIndex < cards.length) {
        setSelectedOptimization(cards[newIndex]);
        setCurrentOptimizationIndex(newIndex);
      }
    }
  }, [selectedObjectiveId, getOptimizationCardsForObjective]);

  // Handler for rating success - updates local state after API call succeeds
  const handleRatingSuccess = useCallback((optimizationId, is_liked, updates = {}) => {
    setActiveGroupsForObjectives((prev) =>
      prev.map((g) => ({
        ...g,
        suggestions: (g.suggestions || []).map((s) =>
          s.id === optimizationId ? { ...s, is_liked, ...updates } : s
        ),
      }))
    );

    setSelectedOptimization((prev) =>
      prev?.id === optimizationId ? { ...prev, is_liked, ...updates } : prev
    );
  }, []);

  const cargarObjetivos = useCallback(
    async (options = {}) => {
      const silent = options.silent === true;
      if (!brandActivo) return;
      if (!silent) setError(null);
      try {
        const { error: refetchError } = await refetchObjetivos();
        if (refetchError) {
          setError('Error al cargar los objetivos');
        } else if (!silent) {
          setError(null);
        }
      } catch (err) {
        console.error('Error cargando objetivos:', err);
        setError('Error al cargar los objetivos');
      }
    },
    [brandActivo, refetchObjetivos]
  );

  const handleActualizarValoresActuales = useCallback(async () => {
    if (!brandActivo || refreshingMetricasActuales) return;
    setRefreshingMetricasActuales(true);
    setError(null);
    try {
      await objetivosApi.refreshMetrics(brandActivo.id);
      await cargarObjetivos({ silent: true });
    } catch (err) {
      console.error('Error actualizando valores actuales:', err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'No se pudieron actualizar los valores actuales';
      setError(msg);
    } finally {
      setRefreshingMetricasActuales(false);
    }
  }, [brandActivo, refreshingMetricasActuales, cargarObjetivos]);

  // Función para clasificar objetivos según su estado
  const clasificarObjetivos = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const activos = [];
    const proximos = [];
    const finalizadosPorMes = {}; // Objeto para agrupar por mes/año

    // Parsear fechas (manejo seguro de zona horaria + validación para evitar Invalid Date)
    const parseFecha = (fechaInput) => {
      if (!fechaInput) return null;

      // Si ya viene como Date, validar y normalizar
      if (fechaInput instanceof Date) {
        if (Number.isNaN(fechaInput.getTime())) return null;
        const d = new Date(fechaInput.getTime());
        d.setHours(0, 0, 0, 0);
        return d;
      }

      const fechaStr = String(fechaInput).trim();
      if (!fechaStr) return null;

      // Aceptar YYYY-MM-DD y también ISO con sufijo de tiempo (YYYY-MM-DDTHH:mm:ssZ),
      // parseando solo la parte de fecha para no depender de la zona horaria.
      const m = fechaStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const year = Number(m[1]);
        const month = Number(m[2]);
        const day = Number(m[3]);

        // Validación de rango + validación anti "rollover" (ej: 2024-02-31)
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          const fecha = new Date(year, month - 1, day);
          fecha.setHours(0, 0, 0, 0);
          if (
            fecha.getFullYear() === year &&
            fecha.getMonth() === month - 1 &&
            fecha.getDate() === day
          ) {
            return fecha;
          }
        }
        return null;
      }

      const fecha = new Date(fechaStr);
      if (Number.isNaN(fecha.getTime())) return null;
      fecha.setHours(0, 0, 0, 0);
      return fecha;
    };

    // Función para obtener la clave de agrupación (mes/año)
    const obtenerClaveMes = (fecha) => {
      if (!fecha) return null;
      return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
    };

    let objetivosConFechasInvalidas = 0;

    objetivos.forEach((objetivo) => {
      // Si no tiene fechas, lo dejamos fuera o lo ponemos en una categoría por defecto
      if (!objetivo.fecha_inicio && !objetivo.fecha_fin) {
        // Opcional: puedes decidir dónde poner objetivos sin fechas
        return;
      }

      const fechaInicio = parseFecha(objetivo.fecha_inicio);
      const fechaFin = parseFecha(objetivo.fecha_fin);

      // Si tiene campos de fecha pero no se pudieron parsear, no lo excluimos silenciosamente.
      // Lo mostramos como "activo" para evitar pérdida de datos por fechas malformadas.
      if (!fechaInicio && !fechaFin) {
        objetivosConFechasInvalidas += 1;
        activos.push(objetivo);
        return;
      }

      // Si tiene fecha fin y es anterior a hoy -> FINALIZADO
      if (fechaFin && fechaFin < hoy) {
        // Agrupar por mes/año de la fecha de fin
        const claveMes = obtenerClaveMes(fechaFin);
        if (claveMes) {
          if (!finalizadosPorMes[claveMes]) {
            finalizadosPorMes[claveMes] = [];
          }
          finalizadosPorMes[claveMes].push(objetivo);
        }
        return;
      }

      // Si tiene fecha inicio y es posterior a hoy -> PRÓXIMO
      if (fechaInicio && fechaInicio > hoy) {
        proximos.push(objetivo);
        return;
      }

      // Si llegamos aquí y tiene fecha inicio <= hoy y fecha fin >= hoy (o solo una de las dos) -> ACTIVO
      if ((!fechaInicio || fechaInicio <= hoy) && (!fechaFin || fechaFin >= hoy)) {
        activos.push(objetivo);
        return;
      }
    });

    if (objetivosConFechasInvalidas > 0) {
      console.warn(
        `[Objetivos] ${objetivosConFechasInvalidas} objetivos tienen fechas inválidas/malformadas; se muestran como "activos" para evitar pérdida de datos.`
      );
    }

    // Convertir el objeto agrupado a un array ordenado descendentemente
    const finalizadosAgrupados = Object.entries(finalizadosPorMes)
      .sort((a, b) => b[0].localeCompare(a[0])) // Ordenar descendente por clave (YYYY-MM)
      .map(([clave, objetivos]) => ({
        clave,
        objetivos,
        fechaReferencia: parseFecha(`${clave}-01`) // Usar el primer día del mes como referencia
      }));

    return { activos, proximos, finalizadosAgrupados };
  }, [objetivos]);

  // Función para aplicar filtros a los objetivos clasificados
  const objetivosFiltrados = useMemo(() => {
    const { activos, proximos, finalizadosAgrupados } = clasificarObjetivos;

    // Función helper para verificar si un objetivo pasa los filtros
    const pasaFiltros = (objetivo) => {
      // Filtro de plataforma
      if (filtroPlataforma !== 'todas') {
        const plataformas = objetivo.plataformas_relacionadas || [];
        if (!plataformas.includes(filtroPlataforma)) {
          return false;
        }
      }

      // Filtro de métrica
      if (filtroMetrica !== 'todas') {
        let metricaObjetivo = objetivo.metrica_relacionada || '';
        // Mapear "Presupuesto" a "Inversión" para el filtro
        if (metricaObjetivo === 'Presupuesto') {
          metricaObjetivo = 'Inversión';
        }
        if (metricaObjetivo !== filtroMetrica) {
          return false;
        }
      }

      return true;
    };

    // Filtrar activos
    const activosFiltrados = filtroEstado === 'todos' || filtroEstado === 'activos' 
      ? activos.filter(pasaFiltros)
      : [];

    // Filtrar próximos
    const proximosFiltrados = filtroEstado === 'todos' || filtroEstado === 'proximos'
      ? proximos.filter(pasaFiltros)
      : [];

    // Filtrar finalizados (aplicar filtro a cada grupo)
    const finalizadosAgrupadosFiltrados = filtroEstado === 'todos' || filtroEstado === 'finalizados'
      ? finalizadosAgrupados.map(grupo => ({
          ...grupo,
          objetivos: grupo.objetivos.filter(pasaFiltros)
        })).filter(grupo => grupo.objetivos.length > 0) // Eliminar grupos vacíos
      : [];

    return {
      activos: activosFiltrados,
      proximos: proximosFiltrados,
      finalizadosAgrupados: finalizadosAgrupadosFiltrados
    };
  }, [clasificarObjetivos, filtroEstado, filtroPlataforma, filtroMetrica]);

  const platformCards = useMemo(() => ([
    { value: 'GOOGLE', label: 'Google Ads', isConnected: isGoogleConnected },
    { value: 'META', label: 'Meta Ads', isConnected: isMetaConnected },
  ]), [isGoogleConnected, isMetaConnected]);

  // Función para toggle de secciones
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  // Función para obtener campañas filtradas
  const getFilteredCampaigns = useCallback((plataformaSeleccionada) => {
    let campaigns = [];

    // Agregar campañas de Google si está seleccionado
    if (plataformaSeleccionada === "GOOGLE") {
      campaigns = [...campaigns, ...formatGoogleCampaigns(googleCampaigns)];
    }

    // Agregar campañas de Meta si está seleccionado
    if (plataformaSeleccionada === "META") {
      campaigns = [...campaigns, ...formatMetaCampaigns(metaCampaigns)];
    }

    return campaigns;
  }, [googleCampaigns, metaCampaigns]);

  // Función para cargar campañas usando los hooks existentes
  // Only fetch when campaigns are actually needed (e.g., when selector is opened)
  const cargarCampañas = useCallback(async (force = false) => {
    if (!brandActivo) return;
    
    try {
      // Cargar campañas de Google Ads si está conectado
      if (isGoogleConnected) {
        await fetchGoogleCampaigns({ force });
      }
      
      // Cargar campañas de Meta Ads si está conectado
      if (isMetaConnected) {
        await fetchMetaCampaigns({ force });
      }
    } catch (error) {
      console.error('Error cargando campañas:', error);
      setError('Error al cargar las campañas');
    }
  }, [brandActivo, isGoogleConnected, isMetaConnected, fetchGoogleCampaigns, fetchMetaCampaigns]);

  // Track if campaigns have been loaded to avoid unnecessary refetches
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const isCurrentlyLoadingCampaigns = loadingGoogleCampaigns || loadingMetaCampaigns;

  // Función para filtrar campañas por plataforma
  // This is called when the user needs to select campaigns, so we fetch them here if needed
  const filtrarCampañasPorPlataforma = useCallback(async (plataforma) => {
    // Prevent concurrent fetches - if already loading, wait for it to complete
    if (isCurrentlyLoadingCampaigns) {
      // If campaigns are loading, filter will happen after they load via useEffect
      return;
    }

    // Only fetch campaigns if they haven't been loaded yet
    if (!campaignsLoaded && brandActivo) {
      await cargarCampañas(false);
      setCampaignsLoaded(true);
    }
    
    // Filter campaigns after ensuring they're loaded
    const filtradas = getFilteredCampaigns(plataforma);
    // Ordenar: primero las activas (ACTIVE/ENABLED), luego las demás
    const ordenadas = filtradas.sort((a, b) => {
      const aActiva = a.status === 'ACTIVE' || a.status === 'ENABLED';
      const bActiva = b.status === 'ACTIVE' || b.status === 'ENABLED';
      if (aActiva && !bActiva) return -1;
      if (!aActiva && bActiva) return 1;
      return 0;
    });
    setCampañasFiltradas(ordenadas);
  }, [getFilteredCampaigns, campaignsLoaded, brandActivo, cargarCampañas, isCurrentlyLoadingCampaigns]);

  // Función para filtrar conversiones por texto de búsqueda
  const filtrarConversiones = (texto) => {
    if (!texto.trim()) {
      return [...conversionesDisponibles].sort((a, b) => {
        const aMonthly = Number(a?.monthlyConversions ?? 0) || 0;
        const bMonthly = Number(b?.monthlyConversions ?? 0) || 0;
        if (bMonthly !== aMonthly) return bMonthly - aMonthly;
        return String(a?.nombre ?? '').localeCompare(String(b?.nombre ?? ''), 'es', { sensitivity: 'base' });
      });
    }
    
    const textoLower = texto.toLowerCase();
    return conversionesDisponibles.filter(conversion => 
      conversion && 
      conversion.nombre && 
      conversion.categoria && 
      conversion.tipo && 
      conversion.plataforma &&
      (conversion.nombre.toLowerCase().includes(textoLower) ||
       conversion.categoria.toLowerCase().includes(textoLower) ||
       conversion.tipo.toLowerCase().includes(textoLower) ||
       conversion.plataforma.toLowerCase().includes(textoLower))
    ).sort((a, b) => {
      const aMonthly = Number(a?.monthlyConversions ?? 0) || 0;
      const bMonthly = Number(b?.monthlyConversions ?? 0) || 0;
      if (bMonthly !== aMonthly) return bMonthly - aMonthly;
      return String(a?.nombre ?? '').localeCompare(String(b?.nombre ?? ''), 'es', { sensitivity: 'base' });
    });
  };

  // Función para filtrar campañas por texto de búsqueda
  const filtrarCampañas = (texto) => {
    let resultado = campañasFiltradas;
    
    if (texto.trim()) {
      const textoLower = texto.toLowerCase();
      resultado = campañasFiltradas.filter(campaña => 
        campaña && 
        campaña.nombre && 
        campaña.tipo && 
        campaña.status && 
        campaña.plataforma &&
        (campaña.nombre.toLowerCase().includes(textoLower) ||
         campaña.tipo.toLowerCase().includes(textoLower) ||
         campaña.status.toLowerCase().includes(textoLower) ||
         campaña.plataforma.toLowerCase().includes(textoLower))
      );
    }
    
    // Ordenar: primero las activas (ACTIVE/ENABLED), luego las demás
    return resultado.sort((a, b) => {
      const aActiva = a.status === 'ACTIVE' || a.status === 'ENABLED';
      const bActiva = b.status === 'ACTIVE' || b.status === 'ENABLED';
      if (aActiva && !bActiva) return -1;
      if (!aActiva && bActiva) return 1;
      return 0;
    });
  };

  // Función para obtener métricas de un objetivo específico
  const _obtenerMetricasObjetivo = useCallback(async (objetivoId, startDate, endDate) => {
    setCargandoMetricas(true);
    try {
      const response = await objetivosApi.getObjetivoMetricas(objetivoId, startDate, endDate);
      setObjetivoMetricas(prev => ({
        ...prev,
        [objetivoId]: response.data
      }));
      return response.data;
    } catch (error) {
      console.error('Error obteniendo métricas del objetivo:', error);
      setError('Error al cargar las métricas del objetivo');
      return null;
    } finally {
      setCargandoMetricas(false);
    }
  }, []);

  // Función para obtener conversiones de las cuentas activas
  const obtenerConversionesDisponibles = useCallback(async () => {
    const conversiones = [];
    
    try {
      // Obtener conversiones de Google Ads si está conectado
      if (isGoogleConnected && activeGoogleAccount) {
        const googleConversiones = (activeGoogleAccount.conversion_actions || [])
          ?.filter(action => action && action.status === "ENABLED")
          .map(action => ({
            id: action?.id || '',
            nombre: action?.name || '',
            plataforma: "GOOGLE",
            categoria: action?.category || '',
            tipo: action?.type || '',
            origen: action?.origin || '',
            monthlyConversions: Number(action?.monthly_conversions ?? action?.['metrics.conversions'] ?? 0) || 0,
          })) || [];
        
        conversiones.push(...googleConversiones);
      }
      
      // Obtener conversiones de Meta Ads si está conectado
      if (isMetaConnected && activeMetaAccount) {
        const metaConversiones = (activeMetaAccount.pixels?.data || [])?.flatMap(pixel => 
          (pixel?.existing_events || [])?.map(event => ({
            id: `${pixel?.id || ''}_${event || ''}`,
            nombre: `${pixel?.name || ''} - ${event || ''}`,
            plataforma: "META",
            categoria: "EVENT",
            tipo: event || '',
            origen: "PIXEL",
            pixel_id: pixel?.id || '',
            pixel_name: pixel?.name || '',
            monthlyConversions: Number(pixel?.monthly_event_counts?.[event] ?? 0) || 0,
          })) || []
        ) || [];
        
        conversiones.push(...metaConversiones);
      }
      
      setTodasLasConversiones(conversiones);
      setConversionesDisponibles(conversiones);
    } catch (error) {
      console.error('Error obteniendo conversiones:', error);
      setError('Error al cargar las conversiones disponibles');
    }
  }, [isGoogleConnected, isMetaConnected, activeGoogleAccount, activeMetaAccount]);

  useEffect(() => {
    const handler = () => {
      if (brandActivo?.id != null) invalidateObjetivosQueries(queryClient, brandActivo.id);
    };
    window.addEventListener('objectives-created', handler);
    return () => window.removeEventListener('objectives-created', handler);
  }, [brandActivo?.id, queryClient]);

  // REMOVED: Don't automatically load campaigns on page load
  // Campaigns will only be loaded when:
  // 1. User opens the campaign selector (via filtrarCampañasPorPlataforma)
  // 2. User edits an objective that needs campaigns
  // This prevents unnecessary API calls to /google-ads/campaigns and /meta/campaigns

  // Cargar conversiones cuando haya cuentas activas
  useEffect(() => {
    if ((isGoogleConnected && activeGoogleAccount) || (isMetaConnected && activeMetaAccount)) {
      obtenerConversionesDisponibles();
    }
  }, [isGoogleConnected, isMetaConnected, activeGoogleAccount, activeMetaAccount, obtenerConversionesDisponibles]);

  // Detectar parámetro edit en la URL y abrir el modal
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const objetivoIdToEdit = urlParams.get('edit');
    const shouldOpenCreate = urlParams.get('create') === 'true';
    
    if (objetivoIdToEdit && objetivos.length > 0 && !mostrarModal && brandActivo) {
      const objetivoToEdit = objetivos.find(obj => obj.id === parseInt(objetivoIdToEdit));
      if (objetivoToEdit) {
        // Usar setTimeout para asegurar que el componente esté completamente renderizado
        setTimeout(() => {
          handleEditarObjetivo(objetivoToEdit);
          // Limpiar el parámetro de la URL
          navigate('/objetivos', { replace: true });
        }, 100);
      }
      return;
    }

    // Soportar deep-link desde Home: si no completó el paso de objetivos, verá la vista setup (no abrir modal manual)
    if (shouldOpenCreate && !mostrarModal && brandActivo && isStep2Completed) {
      setTimeout(() => {
        abrirModalCrear();
        navigate('/objetivos', { replace: true });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objetivos, location.search, mostrarModal, navigate, brandActivo, isStep2Completed]);

  // Filtrar campañas automáticamente cuando se avanza al Paso 3 con una plataforma seleccionada
  useEffect(() => {
    if (pasoActual === 3 && formularioObjetivo.plataformas_relacionadas.length > 0) {
      const plataforma = formularioObjetivo.plataformas_relacionadas[0];
      filtrarCampañasPorPlataforma(plataforma);
      setMostrarSelectorCampañas(true);
    }
  }, [pasoActual, formularioObjetivo.plataformas_relacionadas, filtrarCampañasPorPlataforma]);

  // Re-filter campaigns when they finish loading and we're on step 3 with a platform selected
  // This handles the case where filtrarCampañasPorPlataforma was called while campaigns were loading
  const lastFilteredPlatform = useRef(null);
  useEffect(() => {
    if (pasoActual === 3 && 
        !isCurrentlyLoadingCampaigns && 
        campaignsLoaded && 
        formularioObjetivo.plataformas_relacionadas.length > 0 &&
        (googleCampaigns.length > 0 || metaCampaigns.length > 0)) {
      const plataforma = formularioObjetivo.plataformas_relacionadas[0];
      // Only re-filter if platform changed or if we haven't filtered for this platform yet
      if (lastFilteredPlatform.current !== plataforma || campañasFiltradas.length === 0) {
        lastFilteredPlatform.current = plataforma;
        filtrarCampañasPorPlataforma(plataforma);
      }
    }
  }, [pasoActual, isCurrentlyLoadingCampaigns, campaignsLoaded, formularioObjetivo.plataformas_relacionadas, googleCampaigns.length, metaCampaigns.length, campañasFiltradas.length, filtrarCampañasPorPlataforma]);

  const generarNombreObjetivo = useCallback((data) => {
    const metrica = (data?.metrica_relacionada || 'SIN-METRICA').trim();
    const plataforma = (
      data?.plataformas_relacionadas?.[0] ||
      'SIN-PLATAFORMA'
    ).toString().trim();
    return `${metrica} - ${plataforma}`;
  }, []);

  const nombreObjetivoAutogenerado = useMemo(
    () =>
      generarNombreObjetivo({
        metrica_relacionada: formularioObjetivo.metrica_relacionada,
        plataformas_relacionadas: formularioObjetivo.plataformas_relacionadas,
      }),
    [
      formularioObjetivo.metrica_relacionada,
      formularioObjetivo.plataformas_relacionadas,
      generarNombreObjetivo,
    ]
  );

  useEffect(() => {
    setFormularioObjetivo((prev) => {
      const nombreActual = (prev.nombre || '').trim();
      const debeMantenerNombreManual =
        nombreActual.length > 0 && nombreActual !== ultimoNombreAutogenerado;

      if (debeMantenerNombreManual || prev.nombre === nombreObjetivoAutogenerado) {
        return prev;
      }

      return {
        ...prev,
        nombre: nombreObjetivoAutogenerado,
      };
    });
    setUltimoNombreAutogenerado(nombreObjetivoAutogenerado);
  }, [nombreObjetivoAutogenerado, ultimoNombreAutogenerado]);

  const handleCrearObjetivo = async () => {
    // Array para almacenar los errores encontrados
    const errores = [];

    // Validar métrica relacionada
    if (!formularioObjetivo.metrica_relacionada) {
      errores.push('• Métrica relacionada');
    }

    // Validar target
    if (!formularioObjetivo.target || formularioObjetivo.target <= 0) {
      errores.push('• Target (debe ser un número mayor a 0)');
    }

    // Validar plataformas
    if (!formularioObjetivo.plataformas_relacionadas || formularioObjetivo.plataformas_relacionadas.length === 0) {
      errores.push('• Plataformas relacionadas');
    }

    // Validar fechas
    if (!formularioObjetivo.fecha_inicio) {
      errores.push('• Fecha de inicio');
    }

    if (!formularioObjetivo.fecha_fin) {
      errores.push('• Fecha de fin');
    }

    // Validar conversiones para métricas que las requieren
    if ((formularioObjetivo.metrica_relacionada === 'Conversiones' || 
         formularioObjetivo.metrica_relacionada === 'ROAS' || 
         formularioObjetivo.metrica_relacionada === 'CPA') && 
        (!formularioObjetivo.conversiones_seleccionadas || formularioObjetivo.conversiones_seleccionadas.length === 0)) {
      errores.push('• Conversiones específicas (requeridas para esta métrica)');
    }

    // Validar fechas si ambas están presentes
    if (formularioObjetivo.fecha_inicio && formularioObjetivo.fecha_fin) {
      const fechaInicio = new Date(formularioObjetivo.fecha_inicio);
      const fechaFin = new Date(formularioObjetivo.fecha_fin);
      
      if (fechaInicio > fechaFin) {
        errores.push('• La fecha de inicio debe ser anterior a la fecha de fin');
      }
    }

    // Si hay errores, mostrarlos todos y no continuar
    if (errores.length > 0) {
      const mensajeError = `Por favor completa los siguientes campos requeridos:\n\n${errores.join('\n')}`;
      setError(mensajeError);
      return;
    }

    if (submittingObjetivo) return;
    setSubmittingObjetivo(true);

    const metricaCreada = formularioObjetivo.metrica_relacionada;
    const plataformasCreadas = [...(formularioObjetivo.plataformas_relacionadas || [])];

    try {
      // Preparar datos para enviar - convertir conversiones_seleccionadas a conversiones_relacionadas
      const objetivoData = {
        ...formularioObjetivo,
        nombre: (formularioObjetivo.nombre || nombreObjetivoAutogenerado).trim(),
        brand_id: brandActivo.id,
        conversiones_relacionadas: formularioObjetivo.conversiones_seleccionadas
      };

      // Para métricas monetarias, guardar la moneda ISO correspondiente a la plataforma elegida
      if (formularioObjetivo.metrica_relacionada === 'Presupuesto' || formularioObjetivo.metrica_relacionada === 'CPA') {
        objetivoData.currency = monedaInputISO;
      }
      // Remover el campo conversiones_seleccionadas ya que el backend espera conversiones_relacionadas
      delete objetivoData.conversiones_seleccionadas;

      await objetivosApi.createObjetivo(objetivoData);
      
      // Recargar objetivos
      await cargarObjetivos();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('objectives-created'));
      }

      if (
        (metricaCreada === 'ROAS' || metricaCreada === 'CPA') &&
        brandActivo?.id
      ) {
        const platforms = [
          ...new Set(
            plataformasCreadas
              .map(plataformaObjetivoToOptimizationPlatform)
              .filter(Boolean)
          ),
        ];
        if (platforms.length > 0) {
          void requestGeneration({
            brandId: brandActivo.id,
            platforms,
            loadCount: async () => {
              const response = await optimizationsApi.getOptimizations();
              const { activeGroups, expiredGroups } = normalizeSuggestionsResponse(
                response?.data
              );
              return activeGroups.length + expiredGroups.length;
            },
          });
        }
      }
      
      // Limpiar formulario
      setFormularioObjetivo({
        nombre: '',
        metrica_relacionada: '',
        plataformas_relacionadas: [],
        campanas_relacionadas: [],
        conversiones_seleccionadas: [],
        fecha_inicio: '',
        fecha_fin: '',
        target: ''
      });
      setMostrarModal(false);
      setMostrarSelectorCampañas(false);
      setMostrarSelectorConversiones(false);
      setConversionesSeleccionadas([]);
      setFiltroConversiones('');
      setFiltroCampañas('');
      setError(null);
      
    } catch (error) {
      console.error('Error creando objetivo:', error);
      setError(error.response?.data?.error || 'Error al crear el objetivo');
    } finally {
      setSubmittingObjetivo(false);
    }
  };

  const handleActualizarObjetivo = async () => {
    // Array para almacenar los errores encontrados
    const errores = [];

    // Validar target
    if (!formularioObjetivo.target || formularioObjetivo.target <= 0) {
      errores.push('• Target (debe ser un número mayor a 0)');
    }

    // Validar fechas si ambas están presentes
    if (formularioObjetivo.fecha_inicio && formularioObjetivo.fecha_fin) {
      const fechaInicio = new Date(formularioObjetivo.fecha_inicio);
      const fechaFin = new Date(formularioObjetivo.fecha_fin);
      
      if (fechaInicio > fechaFin) {
        errores.push('• La fecha de inicio debe ser anterior a la fecha de fin');
      }
    }

    // Si hay errores, mostrarlos todos y no continuar
    if (errores.length > 0) {
      const mensajeError = `Por favor completa los siguientes campos requeridos:\n\n${errores.join('\n')}`;
      setError(mensajeError);
      return;
    }

    if (submittingObjetivo) return;
    setSubmittingObjetivo(true);

    try {
      // Preparar datos para enviar - convertir conversiones_seleccionadas a conversiones_relacionadas
      const objetivoData = {
        ...formularioObjetivo,
        nombre: (formularioObjetivo.nombre || nombreObjetivoAutogenerado).trim(),
        conversiones_relacionadas: formularioObjetivo.conversiones_seleccionadas
      };

      // Para métricas monetarias, guardar la moneda ISO correspondiente a la plataforma elegida
      if (formularioObjetivo.metrica_relacionada === 'Presupuesto' || formularioObjetivo.metrica_relacionada === 'CPA') {
        objetivoData.currency = monedaInputISO;
      }
      // Remover el campo conversiones_seleccionadas ya que el backend espera conversiones_relacionadas
      delete objetivoData.conversiones_seleccionadas;
      
      await objetivosApi.updateObjetivo(editandoObjetivo.id, objetivoData);
      
      // Recargar objetivos
      await cargarObjetivos();
      
      // Limpiar formulario
      setFormularioObjetivo({
        nombre: '',
        metrica_relacionada: '',
        plataformas_relacionadas: [],
        campanas_relacionadas: [],
        conversiones_seleccionadas: [],
        fecha_inicio: '',
        fecha_fin: '',
        target: ''
      });
      setEditandoObjetivo(null);
      setMostrarModal(false);
      setMostrarSelectorCampañas(false);
      setMostrarSelectorConversiones(false);
      setConversionesSeleccionadas([]);
      setFiltroConversiones('');
      setFiltroCampañas('');
      setError(null);
      
    } catch (error) {
      console.error('Error actualizando objetivo:', error);
      setError(error.response?.data?.error || 'Error al actualizar el objetivo');
    } finally {
      setSubmittingObjetivo(false);
    }
  };

  const handleEliminarObjetivo = async (objetivoId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este objetivo?')) {
      return;
    }

    try {
      await objetivosApi.deleteObjetivo(objetivoId);
      
      // Recargar objetivos
      await cargarObjetivos();
      
    } catch (error) {
      console.error('Error eliminando objetivo:', error);
      setError(error.response?.data?.error || 'Error al eliminar el objetivo');
    }
  };

  const handleEditarObjetivo = (objetivo) => {
    // Verificar que el objetivo tenga las propiedades necesarias
    if (!objetivo || typeof objetivo !== 'object' || !objetivo.id) {
      console.error('Objetivo inválido para editar:', objetivo);
      setError('Error: Objetivo inválido para editar');
      return;
    }
    
    setEditandoObjetivo(objetivo);
    setPasoActual(1);
    setPresetFechas('personalizado');
    
    // Convertir conversiones_relacionadas a conversiones_seleccionadas
    const conversiones = objetivo.conversiones_relacionadas || objetivo.conversiones_seleccionadas || [];
    
    // Pre-cargar las conversiones seleccionadas
    setConversionesSeleccionadas(conversiones);
    
    // Si tiene plataforma seleccionada, filtrar campañas por esa plataforma
    const plataforma = (objetivo.plataformas_relacionadas && objetivo.plataformas_relacionadas.length > 0) 
      ? objetivo.plataformas_relacionadas[0] 
      : '';
    
    if (plataforma) {
      filtrarCampañasPorPlataforma(plataforma);
      setMostrarSelectorCampañas(true);
    }
    
    // Si la métrica requiere conversiones, mostrar el selector
    const metrica = objetivo.metrica_relacionada;
    if (metrica === 'Conversiones' || metrica === 'ROAS' || metrica === 'CPA') {
      setMostrarSelectorConversiones(true);
    }
    
    setFormularioObjetivo({
      nombre: objetivo.nombre || '',
      metrica_relacionada: objetivo.metrica_relacionada || '',
      plataformas_relacionadas: objetivo.plataformas_relacionadas || [],
      campanas_relacionadas: objetivo.campanas_relacionadas || [],
      conversiones_seleccionadas: conversiones,
      fecha_inicio: objetivo.fecha_inicio || '',
      fecha_fin: objetivo.fecha_fin || '',
      target: objetivo.target || ''
    });
    setMostrarModal(true);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    try {
      // Parsear la fecha manualmente para evitar problemas de zona horaria
      // Si viene en formato "YYYY-MM-DD", parsearlo directamente
      if (typeof fecha === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
        const [year, month, day] = fecha.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      // Si no es formato YYYY-MM-DD, usar el método anterior
      const date = new Date(fecha);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formateando fecha:', error, fecha);
      return 'N/A';
    }
  };

  const formatMetrica = (metrica) => {
    if (!metrica) return 'N/A';
    // Mapear "Presupuesto" a "Inversión" para mantener consistencia con el filtro
    if (metrica === 'Presupuesto') {
      return 'Inversión';
    }
    return metrica;
  };

  const formatTarget = (target, metrica, currency = null) => {
    if (!target && target !== 0) return 'N/A';
    
    try {
      const numTarget = parseFloat(target);
      if (isNaN(numTarget)) return 'N/A';
      
      // Usar la moneda proporcionada o la moneda display por defecto
      const monedaAUsar = currency || monedaDisplay;
      
      // Formatear según el tipo de métrica
      switch (metrica) {
        case 'ROAS':
          return `${numTarget.toFixed(2)}x`;
        case 'CPA':
          return formatCurrencyAmount(numTarget, monedaAUsar, {
            locale: 'es-ES',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        case 'Presupuesto':
          return formatCurrencyAmount(numTarget, monedaAUsar, {
            locale: 'es-ES',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        case 'Conversiones':
          return `${Math.floor(numTarget)}`;
        default:
          return target.toString();
      }
    } catch (error) {
      console.error('Error formateando target:', error, target, metrica);
      return 'N/A';
    }
  };

  // Función para obtener el label del campo target según la métrica
  const obtenerLabelTarget = (metrica) => {
    switch(metrica) {
      case 'ROAS':
        return 'Objetivo de ROAS';
      case 'CPA':
        return `Objetivo de CPA`;
      case 'Conversiones':
        return 'Objetivo de Conversiones';
      case 'Presupuesto':
        return 'Objetivo de Inversión';
      default:
        return 'Objetivo';
    }
  };

  // Función para obtener el placeholder del target según la métrica
  const obtenerPlaceholderTarget = (metrica) => {
    switch(metrica) {
      case 'ROAS':
        return '3.5';
      case 'CPA':
        return '50';
      case 'Conversiones':
        return '100';
      case 'Presupuesto':
        return '1000';
      default:
        return '';
    }
  };

  // Función para aplicar presets de fechas
  const aplicarPresetFechas = (preset) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    let fechaInicio = '';
    let fechaFin = '';
    
    if (preset === 'este_mes') {
      // Primer día del mes
      const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      fechaInicio = primerDia.toISOString().split('T')[0];
      // Último día del mes
      const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
      fechaFin = ultimoDia.toISOString().split('T')[0];
    } else {
      // Personalizado - mantener las fechas actuales o usar hoy como inicio
      fechaInicio = formularioObjetivo.fecha_inicio || hoy.toISOString().split('T')[0];
      fechaFin = formularioObjetivo.fecha_fin || '';
    }
    
    setPresetFechas(preset);
    setFormularioObjetivo({
      ...formularioObjetivo,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin
    });
  };

  // Funciones para manejar el modal
  const abrirModalCrear = () => {
    setEditandoObjetivo(null);
    setPasoActual(1);
    setPresetFechas('personalizado');
    
    // Seleccionar todas las conversiones disponibles por defecto
    const todasLasConversiones = (conversionesDisponibles || []).map(c => c?.id || '');
    setConversionesSeleccionadas(todasLasConversiones);
    
    // Seleccionar todas las campañas disponibles por defecto
    const todasLasCampañas = (campañasFiltradas || []).map(c => c?.id || '');
    
    // Obtener la fecha de hoy en formato YYYY-MM-DD
    const hoy = new Date().toISOString().split('T')[0];
    
    setFormularioObjetivo({
      nombre: '',
      metrica_relacionada: '',
      plataformas_relacionadas: [],
      campanas_relacionadas: todasLasCampañas,
      conversiones_seleccionadas: todasLasConversiones,
      fecha_inicio: hoy,  // Fecha de hoy por defecto
      fecha_fin: '',
      target: ''
    });
    setMostrarModal(true);
    setMostrarSelectorCampañas(false);
    setMostrarSelectorConversiones(false);
    setFiltroConversiones('');
    setFiltroCampañas('');
    setError(null);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setEditandoObjetivo(null);
    setPasoActual(1);
    setPresetFechas('personalizado');
    setMostrarSelectorCampañas(false);
    setMostrarSelectorConversiones(false);
    setConversionesSeleccionadas([]);
    setFiltroConversiones('');
    setFiltroCampañas('');
    setError(null);
  };


  // Componente para renderizar tabla de ACTIVOS
  const renderTablaActivos = () => {
    const { activos } = objetivosFiltrados;
    if (activos.length === 0) return null;

    return (
      <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl shadow-lg overflow-hidden mb-4">
        {/* Header de sección */}
        <div
          onClick={() => toggleSection('activos')}
          className="bg-white/5 backdrop-blur-md border-b border-white/20 px-4 py-3 cursor-pointer hover:bg-white/10 transition-all duration-200"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <ChevronDown
                size={18}
                className={`text-white/70 transition-transform duration-200 ${expandedSections.activos ? 'rotate-180' : ''}`}
              />
              <h3 className="text-sm font-bold text-white">ACTIVOS</h3>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                {activos.length}
              </span>
            </div>
          </div>
        </div>

        {/* Tabla (solo si está expandida) */}
        {expandedSections.activos && (
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-white/10 backdrop-blur-md border-b border-white/20">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-1/5">
                    Objetivo
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-20">
                    Métrica
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-20">
                    Target
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-24">
                    Valor Actual
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-24">
                    Valor Proyectado
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-24">
                    Progreso Real
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-24">
                    Progreso Proyectado
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-24">
                    Período
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-white/80 uppercase tracking-wider w-24">
                    Optimizaciones
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-white/80 uppercase tracking-wider w-20">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/10 backdrop-blur-md">
                {activos.map((objetivo, index) => {
                  const progresoReal = calcularProgreso(objetivo.valor_actual || 0, objetivo.target || 1);
                  const progresoProyectado = calcularProgreso(objetivo.valor_proyectado || 0, objetivo.target || 1);
                  const estilosProyeccion = obtenerEstilosProyeccion(
                    objetivo.valor_proyectado,
                    objetivo.target,
                    objetivo.metrica_relacionada
                  );

                  return (
                    <tr
                      key={objetivo.id}
                      onClick={() => handleEditarObjetivo(objetivo)}
                      className={`hover:bg-white/20 hover:shadow-lg hover:scale-[1.01] transition-all duration-300 group cursor-pointer relative hover:z-10 hover:border-l-4 hover:border-l-fuchsia-500 ${
                        index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'
                      }`}
                    >
                      {/* Objetivo */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="p-1 bg-fuchsia-500/20 rounded">
                            <Target size={12} className="text-fuchsia-400" />
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-white truncate">{objetivo.nombre}</div>
                          </div>
                        </div>
                      </td>

                      {/* Métrica */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {objetivo.metrica_relacionada ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                            {formatMetrica(objetivo.metrica_relacionada)}
                          </span>
                        ) : (
                          <span className="text-xs text-white/50">-</span>
                        )}
                      </td>

                      {/* Target */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {objetivo.target ? (
                          <div className="text-xs font-medium text-white">
                            {formatTarget(objetivo.target, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                          </div>
                        ) : (
                          <span className="text-xs text-white/50">-</span>
                        )}
                      </td>

                      {/* Valor Actual */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {objetivo.valor_actual !== null && objetivo.valor_actual !== undefined ? (
                          <div className="text-xs font-medium text-white">
                            {formatTarget(objetivo.valor_actual, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                          </div>
                        ) : (
                          <span className="text-xs text-white/50">-</span>
                        )}
                      </td>

                      {/* Valor Proyectado */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {objetivo.valor_proyectado !== null && objetivo.valor_proyectado !== undefined ? (
                          <div className={`text-xs font-medium ${estilosProyeccion.color}`}>
                            {formatTarget(objetivo.valor_proyectado, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                          </div>
                        ) : (
                          <span className="text-xs text-white/50">-</span>
                        )}
                      </td>

                      {/* Progreso Real */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-white/20 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-300"
                              style={{ width: `${Math.min(progresoReal, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-white w-12 text-right">
                            {progresoReal.toFixed(1)}%
                          </span>
                        </div>
                      </td>

                      {/* Progreso Proyectado */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-white/20 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full ${estilosProyeccion.bgGradient} transition-all duration-300`}
                              style={{ width: `${Math.min(progresoProyectado, 100)}%` }}
                            />
                          </div>
                          <span className={`text-xs font-medium w-12 text-right ${estilosProyeccion.color}`}>
                            {progresoProyectado.toFixed(1)}%
                          </span>
                        </div>
                      </td>

                      {/* Período */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {(objetivo.fecha_inicio || objetivo.fecha_fin) ? (
                          <div className="text-xs text-white">
                            {objetivo.fecha_inicio && (
                              <div className="flex items-center space-x-1">
                                <span className="text-xs text-white/60">Inicio:</span>
                                <span>{formatFecha(objetivo.fecha_inicio)}</span>
                              </div>
                            )}
                            {objetivo.fecha_fin && (
                              <div className="flex items-center space-x-1">
                                <span className="text-xs text-white/60">Fin:</span>
                                <span>{formatFecha(objetivo.fecha_fin)}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-white/50">-</span>
                        )}
                      </td>

                      {/* Optimizaciones */}
                      <td className="px-3 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                        {getOptimizationTypesCountForObjective(objetivo.id) > 0 ? (
                          <div className="flex justify-center">
                            <button
                              onClick={() => navigate('/optimizador?estado=pendientes')}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-300 border-2 border-green-400 bg-green-400/20 hover:bg-green-400/30 rounded-full transition-all duration-300 shadow-lg shadow-green-500/30 hover:shadow-green-500/50 hover:border-green-300 hover:scale-105"
                            >
                              <span className="mr-1.5 text-base">🚀</span>
                              {getOptimizationTypesCountForObjective(objetivo.id)}
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-white/50">-</span>
                        )}
                      </td>

                      {/* Acciones */}
                      <td className="px-3 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center space-x-1">
                          {/* Botón de Editar */}
                          <button 
                            onClick={() => handleEditarObjetivo(objetivo)}
                            className="p-1 text-white/60 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg transition-all duration-200"
                            title="Editar objetivo"
                          >
                            <Edit2 size={14} />
                          </button>
                          
                          {/* Botón de Eliminar */}
                          <button 
                            onClick={() => handleEliminarObjetivo(objetivo.id)}
                            className="p-1 text-white/60 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                            title="Eliminar objetivo"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Vista móvil para ACTIVOS */}
        {expandedSections.activos && (
          <div className="lg:hidden divide-y divide-white/20">
            {activos.map((objetivo) => {
              const progresoReal = calcularProgreso(objetivo.valor_actual || 0, objetivo.target || 1);
              const progresoProyectado = calcularProgreso(objetivo.valor_proyectado || 0, objetivo.target || 1);
              const estilosProyeccion = obtenerEstilosProyeccion(
                objetivo.valor_proyectado,
                objetivo.target,
                objetivo.metrica_relacionada
              );

              return (
                <div
                  key={objetivo.id}
                  onClick={() => handleEditarObjetivo(objetivo)}
                  className="p-4 hover:bg-white/10 transition-colors duration-200 bg-white/5 cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <div className="p-1 bg-fuchsia-500/20 rounded">
                        <Target size={12} className="text-fuchsia-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-xs font-semibold text-white truncate">{objetivo.nombre}</h3>
                      </div>
                    </div>
                    
                    {/* Botones de acción móvil */}
                    <div className="flex items-center space-x-1 ml-2" onClick={(e) => e.stopPropagation()}>
                      <button 
                        onClick={() => handleEditarObjetivo(objetivo)}
                        className="p-1 text-white/60 hover:text-blue-400 hover:bg-blue-500/20 rounded transition-all duration-200"
                        title="Editar objetivo"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button 
                        onClick={() => handleEliminarObjetivo(objetivo.id)}
                        className="p-1 text-white/60 hover:text-red-400 hover:bg-red-500/20 rounded transition-all duration-200"
                        title="Eliminar objetivo"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                    <div>
                      <div className="text-white/60 mb-1">Métrica</div>
                      {objetivo.metrica_relacionada ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          {formatMetrica(objetivo.metrica_relacionada)}
                        </span>
                      ) : (
                        <span className="text-white/50">-</span>
                      )}
                    </div>
                    <div>
                      <div className="text-white/60 mb-1">Target</div>
                      {objetivo.target ? (
                        <div className="font-medium text-white">
                          {formatTarget(objetivo.target, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                        </div>
                      ) : (
                        <span className="text-white/50">-</span>
                      )}
                    </div>
                    <div>
                      <div className="text-white/60 mb-1">Actual</div>
                      {objetivo.valor_actual !== null && objetivo.valor_actual !== undefined ? (
                        <div className="font-medium text-white">
                          {formatTarget(objetivo.valor_actual, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                        </div>
                      ) : (
                        <span className="text-white/50">-</span>
                      )}
                    </div>
                    <div>
                      <div className="text-white/60 mb-1">Proyectado</div>
                      {objetivo.valor_proyectado !== null && objetivo.valor_proyectado !== undefined ? (
                        <div className={`font-medium ${estilosProyeccion.color}`}>
                          {formatTarget(objetivo.valor_proyectado, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                        </div>
                      ) : (
                        <span className="text-white/50">-</span>
                      )}
                    </div>
                  </div>

                  {/* Barras de progreso móvil */}
                  <div className="space-y-2 mb-3">
                    <div>
                      <div className="flex justify-between text-xs text-white/60 mb-1">
                        <span>Progreso Real</span>
                        <span className="text-white">{progresoReal.toFixed(1)}%</span>
                      </div>
                      <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-300"
                          style={{ width: `${Math.min(progresoReal, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-white/60 mb-1">
                        <span>Progreso Proyectado</span>
                        <span className={estilosProyeccion.color}>{progresoProyectado.toFixed(1)}%</span>
                      </div>
                      <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full ${estilosProyeccion.bgGradient} transition-all duration-300`}
                          style={{ width: `${Math.min(progresoProyectado, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Período */}
                  {objetivo.fecha_inicio && objetivo.fecha_fin && (
                    <div className="text-xs text-white/60">
                      {formatFecha(objetivo.fecha_inicio)} - {formatFecha(objetivo.fecha_fin)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Función para formatear el mes/año como texto (ej: "Noviembre 2025")
  const formatearMesAño = useCallback((claveMes) => {
    if (!claveMes) return '';
    const [year, month] = claveMes.split('-');
    const fecha = new Date(parseInt(year), parseInt(month) - 1, 1);
    return fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long'
    });
  }, []);

  // Componente para renderizar tabla de FINALIZADOS
  const renderTablaFinalizados = () => {
    const { finalizadosAgrupados } = objetivosFiltrados;
    if (!finalizadosAgrupados || finalizadosAgrupados.length === 0) return null;
    
    const totalFinalizados = finalizadosAgrupados.reduce((sum, grupo) => sum + grupo.objetivos.length, 0);

    return (
      <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl shadow-lg overflow-hidden mb-4">
        {/* Header de sección */}
        <div
          onClick={() => toggleSection('finalizados')}
          className="bg-white/5 backdrop-blur-md border-b border-white/20 px-4 py-3 cursor-pointer hover:bg-white/10 transition-all duration-200"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <ChevronDown
                size={18}
                className={`text-white/70 transition-transform duration-200 ${expandedSections.finalizados ? 'rotate-180' : ''}`}
              />
              <h3 className="text-sm font-bold text-white">FINALIZADOS</h3>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/20 text-gray-300 border border-gray-500/30">
                {totalFinalizados}
              </span>
            </div>
          </div>
        </div>

        {/* Tabla (solo si está expandida) */}
        {expandedSections.finalizados && (
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-white/10 backdrop-blur-md border-b border-white/20">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-1/4">
                    Objetivo
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-1/6">
                    Métrica
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-1/6">
                    Target
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-1/6">
                    Valor Final
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-1/4">
                    Período
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-white/80 uppercase tracking-wider w-28">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/10 backdrop-blur-md">
                {finalizadosAgrupados.map((grupo, _grupoIndex) => (
                  <React.Fragment key={grupo.clave}>
                    {/* Header del grupo mensual */}
                    <tr>
                      <td colSpan={6} className="px-4 py-2 bg-white/10 border-t border-white/30 shadow-md">
                        <h4 className="text-xs font-bold text-white uppercase">
                          {formatearMesAño(grupo.clave)}
                        </h4>
                      </td>
                    </tr>
                    {/* Objetivos del grupo */}
                    {grupo.objetivos.map((objetivo, index) => (
                  <tr
                    key={objetivo.id}
                    onClick={() => handleEditarObjetivo(objetivo)}
                    className={`hover:bg-white/20 hover:shadow-lg hover:scale-[1.01] transition-all duration-300 group cursor-pointer relative hover:z-10 hover:border-l-4 hover:border-l-fuchsia-500 ${
                      index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'
                    }`}
                  >
                    {/* Objetivo */}
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className="p-1 bg-fuchsia-500/20 rounded">
                          <Target size={12} className="text-fuchsia-400" />
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-white truncate">{objetivo.nombre}</div>
                        </div>
                      </div>
                    </td>

                    {/* Métrica */}
                    <td className="px-3 py-3 whitespace-nowrap">
                      {objetivo.metrica_relacionada ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          {formatMetrica(objetivo.metrica_relacionada)}
                        </span>
                      ) : (
                        <span className="text-xs text-white/50">-</span>
                      )}
                    </td>

                    {/* Target */}
                    <td className="px-3 py-3 whitespace-nowrap">
                      {objetivo.target ? (
                        <div className="text-xs font-medium text-white">
                          {formatTarget(objetivo.target, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                        </div>
                      ) : (
                        <span className="text-xs text-white/50">-</span>
                      )}
                    </td>

                    {/* Valor Final (valor_actual al cierre) */}
                    <td className="px-3 py-3 whitespace-nowrap">
                      {objetivo.valor_actual !== null && objetivo.valor_actual !== undefined ? (
                        <div className="text-xs font-medium text-white">
                          {formatTarget(objetivo.valor_actual, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                        </div>
                      ) : (
                        <span className="text-xs text-white/50">-</span>
                      )}
                    </td>

                    {/* Período */}
                    <td className="px-3 py-3 whitespace-nowrap">
                      {(objetivo.fecha_inicio || objetivo.fecha_fin) ? (
                        <div className="text-xs text-white">
                          {objetivo.fecha_inicio && (
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-white/60">Inicio:</span>
                              <span>{formatFecha(objetivo.fecha_inicio)}</span>
                            </div>
                          )}
                          {objetivo.fecha_fin && (
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-white/60">Fin:</span>
                              <span>{formatFecha(objetivo.fecha_fin)}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-white/50">-</span>
                      )}
                    </td>

                    {/* Acciones (solo eliminar para finalizados) */}
                    <td className="px-3 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center space-x-1">
                        {/* Botón de Eliminar */}
                        <button 
                          onClick={() => handleEliminarObjetivo(objetivo.id)}
                          className="p-1 text-white/60 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                          title="Eliminar objetivo"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Vista móvil para FINALIZADOS */}
        {expandedSections.finalizados && (
          <div className="lg:hidden">
            {finalizadosAgrupados.map((grupo) => (
              <div key={grupo.clave} className="mb-4">
                {/* Header del grupo mensual móvil */}
                <div className="px-4 py-2 bg-white/10 border-b border-white/30 shadow-md mb-2">
                  <h4 className="text-xs font-bold text-white uppercase">
                    {formatearMesAño(grupo.clave)}
                  </h4>
                </div>
                {/* Objetivos del grupo */}
                <div className="divide-y divide-white/20">
                  {grupo.objetivos.map((objetivo) => (
              <div
                key={objetivo.id}
                onClick={() => handleEditarObjetivo(objetivo)}
                className="p-4 hover:bg-white/10 transition-colors duration-200 bg-white/5 cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <div className="p-1 bg-fuchsia-500/20 rounded">
                      <Target size={12} className="text-fuchsia-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-xs font-semibold text-white truncate">{objetivo.nombre}</h3>
                    </div>
                  </div>
                  
                  {/* Botón de acción móvil (solo eliminar) */}
                  <div className="flex items-center space-x-1 ml-2" onClick={(e) => e.stopPropagation()}>
                    <button 
                      onClick={() => handleEliminarObjetivo(objetivo.id)}
                      className="p-1 text-white/60 hover:text-red-400 hover:bg-red-500/20 rounded transition-all duration-200"
                      title="Eliminar objetivo"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-white/60 mb-1">Métrica</div>
                    {objetivo.metrica_relacionada ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                        {formatMetrica(objetivo.metrica_relacionada)}
                      </span>
                    ) : (
                      <span className="text-white/50">-</span>
                    )}
                  </div>
                  <div>
                    <div className="text-white/60 mb-1">Target</div>
                    {objetivo.target ? (
                      <div className="font-medium text-white">
                        {formatTarget(objetivo.target, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                      </div>
                    ) : (
                      <span className="text-white/50">-</span>
                    )}
                  </div>
                  <div>
                    <div className="text-white/60 mb-1">Valor Final</div>
                    {objetivo.valor_actual !== null && objetivo.valor_actual !== undefined ? (
                      <div className="font-medium text-white">
                        {formatTarget(objetivo.valor_actual, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                      </div>
                    ) : (
                      <span className="text-white/50">-</span>
                    )}
                  </div>
                  <div>
                    <div className="text-white/60 mb-1">Período</div>
                    {objetivo.fecha_inicio && objetivo.fecha_fin ? (
                      <div className="text-white text-xs">
                        {formatFecha(objetivo.fecha_inicio)} - {formatFecha(objetivo.fecha_fin)}
                      </div>
                    ) : (
                      <span className="text-white/50">-</span>
                    )}
                  </div>
                </div>
                  </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Componente para renderizar tabla de PRÓXIMOS
  const renderTablaProximos = () => {
    const { proximos } = objetivosFiltrados;
    if (proximos.length === 0) return null;

    return (
      <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl shadow-lg overflow-hidden mb-4">
        {/* Header de sección */}
        <div
          onClick={() => toggleSection('proximos')}
          className="bg-white/5 backdrop-blur-md border-b border-white/20 px-4 py-3 cursor-pointer hover:bg-white/10 transition-all duration-200"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <ChevronDown
                size={18}
                className={`text-white/70 transition-transform duration-200 ${expandedSections.proximos ? 'rotate-180' : ''}`}
              />
              <h3 className="text-sm font-bold text-white">PRÓXIMOS</h3>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                {proximos.length}
              </span>
            </div>
          </div>
        </div>

        {/* Tabla (solo si está expandida) */}
        {expandedSections.proximos && (
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-white/10 backdrop-blur-md border-b border-white/20">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-1/3">
                    Objetivo
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-1/4">
                    Métrica
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-1/4">
                    Target
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider w-1/3">
                    Período
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-white/80 uppercase tracking-wider w-24">
                    Optimizaciones
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-white/80 uppercase tracking-wider w-28">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/10 backdrop-blur-md">
                {proximos.map((objetivo, index) => (
                  <tr
                    key={objetivo.id}
                    onClick={() => handleEditarObjetivo(objetivo)}
                    className={`hover:bg-white/20 hover:shadow-lg hover:scale-[1.01] transition-all duration-300 group cursor-pointer relative hover:z-10 hover:border-l-4 hover:border-l-fuchsia-500 ${
                      index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'
                    }`}
                  >
                    {/* Objetivo */}
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className="p-1 bg-fuchsia-500/20 rounded">
                          <Target size={12} className="text-fuchsia-400" />
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-white truncate">{objetivo.nombre}</div>
                        </div>
                      </div>
                    </td>

                    {/* Métrica */}
                    <td className="px-3 py-3 whitespace-nowrap">
                      {objetivo.metrica_relacionada ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                          {formatMetrica(objetivo.metrica_relacionada)}
                        </span>
                      ) : (
                        <span className="text-xs text-white/50">-</span>
                      )}
                    </td>

                    {/* Target */}
                    <td className="px-3 py-3 whitespace-nowrap">
                      {objetivo.target ? (
                        <div className="text-xs font-medium text-white">
                          {formatTarget(objetivo.target, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                        </div>
                      ) : (
                        <span className="text-xs text-white/50">-</span>
                      )}
                    </td>

                    {/* Período */}
                    <td className="px-3 py-3 whitespace-nowrap">
                      {(objetivo.fecha_inicio || objetivo.fecha_fin) ? (
                        <div className="text-xs text-white">
                          {objetivo.fecha_inicio && (
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-white/60">Inicio:</span>
                              <span>{formatFecha(objetivo.fecha_inicio)}</span>
                            </div>
                          )}
                          {objetivo.fecha_fin && (
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-white/60">Fin:</span>
                              <span>{formatFecha(objetivo.fecha_fin)}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-white/50">-</span>
                      )}
                    </td>

                    {/* Optimizaciones */}
                    <td className="px-3 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                              {getOptimizationTypesCountForObjective(objetivo.id) > 0 ? (
                        <div className="flex justify-center">
                          <button
                                    onClick={() => navigate('/optimizador?estado=pendientes')}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-300 border-2 border-green-400 bg-green-400/20 hover:bg-green-400/30 rounded-full transition-all duration-300 shadow-lg shadow-green-500/30 hover:shadow-green-500/50 hover:border-green-300 hover:scale-105"
                          >
                            <span className="mr-1.5 text-base">🚀</span>
                                    {getOptimizationTypesCountForObjective(objetivo.id)}
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-white/50">-</span>
                      )}
                    </td>

                    {/* Acciones */}
                    <td className="px-3 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center space-x-1">
                        {/* Botón de Editar */}
                        <button 
                          onClick={() => handleEditarObjetivo(objetivo)}
                          className="p-1 text-white/60 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg transition-all duration-200"
                          title="Editar objetivo"
                        >
                          <Edit2 size={14} />
                        </button>
                        
                        {/* Botón de Eliminar */}
                        <button 
                          onClick={() => handleEliminarObjetivo(objetivo.id)}
                          className="p-1 text-white/60 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                          title="Eliminar objetivo"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Vista móvil para PRÓXIMOS */}
        {expandedSections.proximos && (
          <div className="lg:hidden divide-y divide-white/20">
            {proximos.map((objetivo) => (
              <div
                key={objetivo.id}
                onClick={() => handleEditarObjetivo(objetivo)}
                className="p-4 hover:bg-white/10 transition-colors duration-200 bg-white/5 cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <div className="p-1 bg-fuchsia-500/20 rounded">
                      <Target size={12} className="text-fuchsia-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-xs font-semibold text-white truncate">{objetivo.nombre}</h3>
                    </div>
                  </div>
                  
                  {/* Botones de acción móvil */}
                  <div className="flex items-center space-x-1 ml-2" onClick={(e) => e.stopPropagation()}>
                    <button 
                      onClick={() => handleEditarObjetivo(objetivo)}
                      className="p-1 text-white/60 hover:text-blue-400 hover:bg-blue-500/20 rounded transition-all duration-200"
                      title="Editar objetivo"
                    >
                      <Edit2 size={12} />
                    </button>
                    <button 
                      onClick={() => handleEliminarObjetivo(objetivo.id)}
                      className="p-1 text-white/60 hover:text-red-400 hover:bg-red-500/20 rounded transition-all duration-200"
                      title="Eliminar objetivo"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-white/60 mb-1">Métrica</div>
                    {objetivo.metrica_relacionada ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                        {formatMetrica(objetivo.metrica_relacionada)}
                      </span>
                    ) : (
                      <span className="text-white/50">-</span>
                    )}
                  </div>
                  <div>
                    <div className="text-white/60 mb-1">Target</div>
                    {objetivo.target ? (
                      <div className="font-medium text-white">
                        {formatTarget(objetivo.target, objetivo.metrica_relacionada, getCurrencyForObjetivo(objetivo))}
                      </div>
                    ) : (
                      <span className="text-white/50">-</span>
                    )}
                  </div>
                  <div className="col-span-2">
                    <div className="text-white/60 mb-1">Período</div>
                    {objetivo.fecha_inicio && objetivo.fecha_fin ? (
                      <div className="text-white text-xs">
                        {formatFecha(objetivo.fecha_inicio)} - {formatFecha(objetivo.fecha_fin)}
                      </div>
                    ) : (
                      <span className="text-white/50">-</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Si no hay ninguna cuenta conectada, mostrar título, descripción y estado vacío con acciones
  if (!hasAnyConnection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 pt-[5rem] px-6 pb-6">
        <div className="max-w-7xl mx-auto">
          <NoAdsConnectedSection
            connectGoogleAds={connectGoogleAds}
            startMetaOAuth={startMetaOAuth}
            activePlatform={'all'}
            activeGoogleAccount={null}
            activeMetaAccount={null}
            context={'objetivos'}
          />
        </div>
      </div>
    );
  }

  // Usuario tiene cuentas conectadas pero no completó el paso de definir objetivos: misma vista que en Home.
  // OJO: `setup_steps_completed` es un flag a NIVEL USUARIO (onboarding global), mientras que los objetivos
  // son POR MARCA. Si la marca activa ya tiene objetivos (creados por admin/API, en otra marca, o si la
  // marca del flag de setup falló silenciosamente al guardarse), debemos mostrar la tabla, no el setup.
  // Por eso solo mostramos la vista de setup cuando la marca está realmente vacía (y ya terminó de cargar,
  // para no parpadear el setup en marcas que sí tienen objetivos).
  if (hasAnyConnection && !isStep2Completed && !loading && objetivos.length === 0) {
    const toggleConversionSetup = (id) => {
      setSelectedConversionsForSetup((prev) =>
        prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
      );
    };
    const selectAllForPlatformSetup = (platform) => {
      const ids = platform === 'GOOGLE'
        ? googleConversionsSetup.map((c) => c.id)
        : metaConversionsSetup.map((c) => c.id);
      setSelectedConversionsForSetup((prev) => {
        const without = prev.filter((p) => !ids.includes(p));
        const allSelected = ids.every((id) => prev.includes(id));
        return allSelected ? without : [...new Set([...without, ...ids])];
      });
    };
    const handleGenerateSetup = () => {
      setIsCreateObjectivesSetupModalOpen(true);
    };

    const toggleObjectiveMetricSetup = (metrica) => {
      setSelectedObjectiveMetricsForSetup((prev) =>
        prev.includes(metrica) ? prev.filter((m) => m !== metrica) : [...prev, metrica]
      );
    };
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 pt-[5rem] px-3 pb-3 lg:px-6 lg:pb-6">
        <div className="max-w-7xl mx-auto">
          <ObjectivesSetupView
            googleConversions={googleConversionsSetup}
            metaConversions={metaConversionsSetup}
            selectedConversions={selectedConversionsForSetup}
            onToggleConversion={toggleConversionSetup}
            onSelectAllForPlatform={selectAllForPlatformSetup}
            selectedObjectiveMetrics={selectedObjectiveMetricsForSetup}
            onToggleObjectiveMetric={toggleObjectiveMetricSetup}
            onGenerate={handleGenerateSetup}
            loading={loadingGoogleAccount || loadingMetaAccount}
            isGoogleConnected={isGoogleConnected}
            isMetaConnected={isMetaConnected}
          />
        </div>
        <CreateObjectivesModal
          isOpen={isCreateObjectivesSetupModalOpen}
          selectedConversions={selectedConversionsForSetup}
          selectedObjectiveMetrics={selectedObjectiveMetricsForSetup}
          autoGenerateOnOpen={true}
          onClose={() => {
            setIsCreateObjectivesSetupModalOpen(false);
            setSelectedConversionsForSetup([]);
            setSelectedObjectiveMetricsForSetup([]);
            void cargarObjetivos();
          }}
          onSuccess={async () => {
            const steps = auth?.user?.setup_steps_completed || [];
            if (!steps.includes(setupSteps.CREA_OBJETIVOS_AUTOMATICOS_1)) {
              const updated = [...steps, setupSteps.CREA_OBJETIVOS_AUTOMATICOS_1];
              updateContextUser({ setup_steps_completed: updated });
              usersApi.setSetupSteps(updated).catch(() => {});
            }
            await cargarObjetivos();
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 pt-20 px-3 pb-3 lg:px-6 lg:pb-6">
      <div className="max-w-7xl mx-auto">
        <ObjetivosFilterBar
          filtroPlataforma={filtroPlataforma}
          setFiltroPlataforma={setFiltroPlataforma}
          filtroEstado={filtroEstado}
          setFiltroEstado={setFiltroEstado}
          filtroMetrica={filtroMetrica}
          setFiltroMetrica={setFiltroMetrica}
          onCreate={abrirModalCrear}
          disabled={!brandActivo}
        />
        {/* Mensaje de error */}
        {error && !mostrarModal && (
          <div className="backdrop-blur-md bg-red-500/20 border border-red-400/30 rounded-xl p-4 mb-6 shadow-lg">
            <div className="flex items-start">
              <AlertCircle size={20} className="text-red-400 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-red-200 whitespace-pre-line">{error}</div>
            </div>
          </div>
        )}

        {mostrarModal && (
          <div className="fixed inset-0 modal-safe-area bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4">
            <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 border border-white/20 rounded-2xl shadow-2xl w-full max-w-5xl lg:max-w-[min(64rem,calc(100vw-6rem-1rem))] max-h-[90vh] overflow-hidden mx-2 sm:mx-0 flex flex-col">
              {/* Header del modal */}
              <div className="p-3 sm:p-4 border-b border-white/20">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0 pr-3">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5">
                      {editandoObjetivo ? 'Editar Objetivo' : 'Crear Nuevo Objetivo'}
                    </h3>
                    {/* Indicador de pasos */}
                    <div className="flex items-center space-x-1.5 mt-2">
                      {[1, 2, 3, 4].map((paso) => (
                        <React.Fragment key={paso}>
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold transition-all duration-300 ${
                            pasoActual === paso
                              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg scale-110'
                              : pasoActual > paso
                              ? 'bg-green-500/30 text-green-300 border border-green-400/30'
                              : 'bg-white/10 text-white/50 border border-white/20'
                          }`}>
                            {pasoActual > paso ? <Check size={12} /> : paso}
                          </div>
                          {paso < 4 && (
                            <div className={`h-0.5 w-8 transition-all duration-300 ${
                              pasoActual > paso ? 'bg-green-500/50' : 'bg-white/20'
                            }`} />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={cerrarModal}
                    className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all duration-300 flex-shrink-0 backdrop-blur-sm border border-white/10 hover:border-white/20"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Contenido del modal con scroll */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4">
                {error && (
                  <div className="sticky top-0 z-20 mb-3">
                    <div className="backdrop-blur-md bg-red-500/20 border border-red-400/30 rounded-xl p-3 shadow-lg">
                      <div className="flex items-start">
                        <AlertCircle size={18} className="text-red-300 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="text-red-200 text-xs sm:text-sm whitespace-pre-line">
                          {error}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Paso 1: Selección de métrica, target, fechas y plataforma */}
                {pasoActual === 1 && (
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">Seleccioná tu objetivo principal</h4>
                    </div>

                    {/* Selector de métricas simple */}
                    <div className="backdrop-blur-xl bg-white/5 border border-white/20 rounded-lg divide-y divide-white/10">
                      {METRICAS_CARDS.map((metrica) => {
                        const isSelected = formularioObjetivo.metrica_relacionada === metrica.value;
                        return (
                          <button
                            key={metrica.value}
                            onClick={() => {
                              const nuevaMetrica = metrica.value;
                              setFormularioObjetivo({...formularioObjetivo, metrica_relacionada: nuevaMetrica});
                              
                              // Mostrar selector de conversiones si aplica
                              if (nuevaMetrica === 'Conversiones' || nuevaMetrica === 'ROAS' || nuevaMetrica === 'CPA') {
                                setMostrarSelectorConversiones(true);
                              } else {
                                setMostrarSelectorConversiones(false);
                                setConversionesSeleccionadas([]);
                              }
                              
                              setFormularioObjetivo({
                                ...formularioObjetivo,
                                metrica_relacionada: nuevaMetrica,
                                conversiones_seleccionadas: (nuevaMetrica === 'Conversiones' || nuevaMetrica === 'ROAS' || nuevaMetrica === 'CPA') 
                                  ? conversionesSeleccionadas 
                                  : []
                              });
                            }}
                            className={`w-full px-3 py-2 text-left transition-all duration-200 ${
                              isSelected 
                                ? 'bg-white/10 text-white' 
                                : 'text-white/70 hover:text-white hover:bg-white/5'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium">{metrica.label}</span>
                              {isSelected && (
                                <Check size={12} className="text-white" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Campo dinámico de target */}
                    {formularioObjetivo.metrica_relacionada && (
                      <div className="space-y-1.5">
                        <label className="block text-xs font-medium text-white/90">
                          {obtenerLabelTarget(formularioObjetivo.metrica_relacionada)} *
                        </label>
                        <div className="relative">
                          {(formularioObjetivo.metrica_relacionada === 'Presupuesto' || formularioObjetivo.metrica_relacionada === 'CPA') && Boolean(monedaInputISO) && (
                            <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-white/60 font-medium pointer-events-none">
                              {monedaInputISO}
                            </span>
                          )}
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formularioObjetivo.target}
                            onChange={(e) => setFormularioObjetivo({...formularioObjetivo, target: e.target.value})}
                            className={`w-full px-2 py-1.5 text-xs border border-white/20 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/5 hover:border-white/30 text-white placeholder-white/40 ${
                              (formularioObjetivo.metrica_relacionada === 'Presupuesto' || formularioObjetivo.metrica_relacionada === 'CPA') && Boolean(monedaInputISO) ? 'pl-10' : 'pl-2'
                            }`}
                            placeholder={obtenerPlaceholderTarget(formularioObjetivo.metrica_relacionada)}
                          />
                        </div>
                      </div>
                    )}

                    {/* Presets de fechas */}
                    {formularioObjetivo.metrica_relacionada && (
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-white/90">
                          Rango de fechas *
                        </label>
                        
                        {/* Presets */}
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { value: 'este_mes', label: 'Este mes' },
                            { value: 'personalizado', label: 'Personalizado' }
                          ].map((preset) => (
                            <button
                              key={preset.value}
                              onClick={() => aplicarPresetFechas(preset.value)}
                              className={`px-2 py-1.5 rounded text-xs font-medium transition-all duration-200 border ${
                                presetFechas === preset.value
                                  ? 'bg-blue-500/30 text-blue-100 border-blue-400/50'
                                  : 'bg-white/5 text-white/80 border-white/20 hover:bg-white/10 hover:border-white/30'
                              }`}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>

                        {/* Date pickers */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1">Fecha de Inicio</label>
                            <input
                              type="date"
                              value={formularioObjetivo.fecha_inicio}
                              onChange={(e) => {
                                setFormularioObjetivo({...formularioObjetivo, fecha_inicio: e.target.value});
                                setPresetFechas('personalizado');
                              }}
                              className="w-full px-2 py-1.5 text-xs border border-white/20 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/5 hover:border-white/30 text-white cursor-pointer"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-white/70 mb-1">Fecha de Fin</label>
                            <input
                              type="date"
                              value={formularioObjetivo.fecha_fin}
                              onChange={(e) => {
                                setFormularioObjetivo({...formularioObjetivo, fecha_fin: e.target.value});
                                setPresetFechas('personalizado');
                              }}
                              className="w-full px-2 py-1.5 text-xs border border-white/20 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/5 hover:border-white/30 text-white cursor-pointer"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Selector de plataformas */}
                    {formularioObjetivo.metrica_relacionada && (
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-white/90">
                          Plataforma *
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {PLATAFORMAS_OPCIONES.map((plataforma) => {
                            const isSelected = formularioObjetivo.plataformas_relacionadas.includes(plataforma.value);
                            return (
                              <button
                                key={plataforma.value}
                                onClick={() => {
                                  const nuevaPlataforma = plataforma.value;
                                  
                                  // Filtrar conversiones según la plataforma seleccionada si requiere conversiones
                                  if (mostrarSelectorConversiones) {
                                    const conversionesFiltradas = todasLasConversiones.filter(
                                      (conv) => conv.plataforma === nuevaPlataforma
                                    );
                                    setConversionesDisponibles(conversionesFiltradas);
                                    
                                    // Limpiar conversiones seleccionadas cuando cambia la plataforma
                                    setConversionesSeleccionadas([]);
                                  }
                                  
                                  setFormularioObjetivo({
                                    ...formularioObjetivo,
                                    plataformas_relacionadas: nuevaPlataforma ? [nuevaPlataforma] : [],
                                    conversiones_seleccionadas: mostrarSelectorConversiones ? [] : formularioObjetivo.conversiones_seleccionadas
                                  });
                                }}
                                className={`px-2 py-1.5 rounded text-xs font-medium transition-all duration-200 border ${
                                  isSelected
                                    ? 'bg-blue-500/30 text-blue-100 border-blue-400/50'
                                    : 'bg-white/5 text-white/80 border-white/20 hover:bg-white/10 hover:border-white/30'
                                }`}
                              >
                                {plataforma.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Paso 2: Selección de conversiones (solo si ROAS, CPA o Conversiones) */}
                {pasoActual === 2 && mostrarSelectorConversiones && formularioObjetivo.metrica_relacionada && (
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">Seleccioná las métricas para medir el {formularioObjetivo.metrica_relacionada}</h4>
                    </div>

                    <div className="space-y-2">
                      {conversionesDisponibles.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-xs text-white/60">
                            No hay conversiones disponibles para la plataforma seleccionada.
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* Opciones rápidas */}
                          <div className="mb-2 space-y-1.5">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="conversiones_opcion"
                                value="todas"
                                checked={conversionesSeleccionadas.length === conversionesDisponibles.length && conversionesDisponibles.length > 0}
                                onChange={() => {
                                  const todasLasConversiones = (conversionesDisponibles || []).map(c => c?.id || '');
                                  setConversionesSeleccionadas(todasLasConversiones);
                                  setFormularioObjetivo({
                                    ...formularioObjetivo,
                                    conversiones_seleccionadas: todasLasConversiones
                                  });
                                }}
                                className="mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-white/20"
                              />
                              <span className="text-xs text-white/80">
                                Todas las conversiones ({conversionesDisponibles.length})
                              </span>
                            </label>
                            
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="conversiones_opcion"
                                value="personalizadas"
                                checked={conversionesSeleccionadas.length !== conversionesDisponibles.length || conversionesDisponibles.length === 0}
                                onChange={() => {
                                  setConversionesSeleccionadas([]);
                                  setFormularioObjetivo({
                                    ...formularioObjetivo,
                                    conversiones_seleccionadas: []
                                  });
                                }}
                                className="mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-white/20"
                              />
                              <span className="text-xs text-white/80">Seleccionar conversiones específicas</span>
                            </label>
                          </div>
                          
                          {/* Layout de dos columnas */}
                          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                            {/* Columna 1: Conversiones disponibles */}
                            <div className="flex flex-col">
                              <h4 className="text-xs font-medium text-white/80 mb-1.5 flex items-center">
                                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5"></span>
                                Conversiones Disponibles ({conversionesDisponibles.length})
                              </h4>
                              
                              {/* Buscador de conversiones */}
                              <div className="mb-2">
                                <div className="relative">
                                  <input
                                    type="text"
                                    placeholder="Buscar conversiones..."
                                    value={filtroConversiones}
                                    onChange={(e) => setFiltroConversiones(e.target.value)}
                                    className="w-full px-2 py-1.5 pl-6 text-xs border border-white/20 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/5 hover:border-white/30 text-white placeholder-white/40"
                                  />
                                  <svg className="absolute left-1.5 top-1.5 w-3 h-3 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                  </svg>
                                </div>
                              </div>
                              
                              <div className="flex-1 h-96 overflow-y-auto border border-white/20 rounded p-2 space-y-1.5 bg-white/5">
                                {filtrarConversiones(filtroConversiones).length === 0 ? (
                                  <div className="text-center py-4">
                                    <p className="text-xs text-white/50">
                                      {filtroConversiones ? 'No se encontraron conversiones' : 'No hay conversiones disponibles'}
                                    </p>
                                  </div>
                                ) : (
                                  (filtrarConversiones(filtroConversiones) || []).map(conversion => (
                                  <label key={conversion.id} className="flex items-center p-1.5 hover:bg-white/10 rounded transition-colors cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={conversionesSeleccionadas.includes(conversion.id)}
                                      onChange={(e) => {
                                        const nuevasConversiones = e.target.checked
                                          ? [...conversionesSeleccionadas, conversion.id]
                                          : conversionesSeleccionadas.filter(id => id !== conversion.id);
                                        setConversionesSeleccionadas(nuevasConversiones);
                                        setFormularioObjetivo({
                                          ...formularioObjetivo,
                                          conversiones_seleccionadas: nuevasConversiones
                                        });
                                      }}
                                      className="mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-white/20 rounded"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs text-white/80 truncate">{conversion.nombre}</div>
                                      <div className="flex items-center space-x-1.5 mt-0.5">
                                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                                          conversion.plataforma === 'GOOGLE' 
                                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                                            : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                                        }`}>
                                          {conversion.plataforma}
                                        </span>
                                      </div>
                                    </div>
                                  </label>
                                  ))
                                )}
                              </div>
                            </div>
                            
                            {/* Columna 2: Conversiones seleccionadas */}
                            <div className="flex flex-col">
                              <h4 className="text-xs font-medium text-white/80 mb-1.5 flex items-center">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5"></span>
                                Conversiones Seleccionadas ({conversionesSeleccionadas.length})
                              </h4>
                              {/* Espacio para alinear con el buscador de la columna izquierda */}
                              <div className="mb-2 h-[38px]"></div>
                              <div className="flex-1 h-96 overflow-y-auto border border-white/20 rounded p-2 space-y-1.5 bg-white/5">
                                {conversionesSeleccionadas.length === 0 ? (
                                  <div className="text-center py-8">
                                    <p className="text-xs text-white/50">No hay conversiones seleccionadas</p>
                                  </div>
                                ) : (
                                  (conversionesSeleccionadas || []).map(conversionId => {
                                    const conversion = conversionesDisponibles.find(c => c.id === conversionId);
                                    return conversion ? (
                                      <div key={conversionId} className="flex items-center justify-between p-1.5 bg-green-500/10 rounded border border-green-500/20">
                                        <div className="flex-1 min-w-0">
                                          <div className="text-xs text-white/80 truncate">{conversion.nombre}</div>
                                        </div>
                                        <button
                                          onClick={() => {
                                            const nuevasConversiones = conversionesSeleccionadas.filter(id => id !== conversionId);
                                            setConversionesSeleccionadas(nuevasConversiones);
                                            setFormularioObjetivo({
                                              ...formularioObjetivo,
                                              conversiones_seleccionadas: nuevasConversiones
                                            });
                                          }}
                                          className="ml-2 p-0.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                        </button>
                                      </div>
                                    ) : null;
                                  })
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Paso 3: Selección de campañas */}
                {pasoActual === 3 && (
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">¿Qué campañas deseas incluir en el objetivo?</h4>
                    </div>

                    {/* Selector de Campañas */}
                    {formularioObjetivo.plataformas_relacionadas.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-white/60">
                          Selecciona las campañas que se incluirán en este objetivo
                        </p>
                        
                        {(loadingGoogleCampaigns || loadingMetaCampaigns) ? (
                          <div className="text-center py-4">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto mb-1.5"></div>
                            <p className="text-xs text-white/60">Cargando campañas...</p>
                          </div>
                        ) : (
                          <>
                            {/* Opciones rápidas */}
                            <div className="mb-2 space-y-1.5">
                              <label className="flex items-center">
                                <input
                                  type="radio"
                                  name="campañas_opcion"
                                  value="todas"
                                  checked={formularioObjetivo.campanas_relacionadas.length === campañasFiltradas.length && campañasFiltradas.length > 0}
                                  onChange={() => {
                                    const todasLasCampañas = (campañasFiltradas || []).map(c => c?.id || '');
                                    setFormularioObjetivo({
                                      ...formularioObjetivo,
                                      campanas_relacionadas: todasLasCampañas
                                    });
                                  }}
                                  className="mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-white/20"
                                />
                                <span className="text-xs text-white/80">
                                  Campañas actuales y futuras ({campañasFiltradas.length})
                                </span>
                              </label>
                              
                              <label className="flex items-center">
                                <input
                                  type="radio"
                                  name="campañas_opcion"
                                  value="personalizadas"
                                  checked={formularioObjetivo.campanas_relacionadas.length !== campañasFiltradas.length || campañasFiltradas.length === 0}
                                  onChange={() => {
                                    setFormularioObjetivo({
                                      ...formularioObjetivo,
                                      campanas_relacionadas: []
                                    });
                                  }}
                                  className="mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-white/20"
                                />
                                <span className="text-xs text-white/80">Seleccionar campañas específicas</span>
                              </label>
                            </div>
                            
                            {/* Layout de dos columnas */}
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                              {/* Columna 1: Campañas disponibles */}
                              <div className="flex flex-col">
                                <h4 className="text-xs font-medium text-white/80 mb-1.5 flex items-center">
                                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5"></span>
                                  Campañas Disponibles ({campañasFiltradas.length})
                                </h4>
                                
                                {/* Buscador de campañas */}
                                <div className="mb-2">
                                  <div className="relative">
                                    <input
                                      type="text"
                                      placeholder="Buscar campañas..."
                                      value={filtroCampañas}
                                      onChange={(e) => setFiltroCampañas(e.target.value)}
                                      className="w-full px-2 py-1.5 pl-6 text-xs border border-white/20 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/5 hover:border-white/30 text-white placeholder-white/40"
                                    />
                                    <svg className="absolute left-1.5 top-1.5 w-3 h-3 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                  </div>
                                </div>
                                
                                <div className="flex-1 h-96 overflow-y-auto border border-white/20 rounded p-2 space-y-1.5 bg-white/5">
                                  {filtrarCampañas(filtroCampañas).length === 0 ? (
                                    <div className="text-center py-8">
                                      <p className="text-xs text-white/60">
                                        {filtroCampañas ? 'No se encontraron campañas' : 'No hay campañas disponibles'}
                                      </p>
                                    </div>
                                  ) : (
                                    (filtrarCampañas(filtroCampañas) || []).map(campaña => (
                                      <label key={campaña.id} className="flex items-center p-1.5 hover:bg-white/10 rounded transition-colors cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={formularioObjetivo.campanas_relacionadas.includes(campaña.id)}
                                          onChange={(e) => {
                                            const nuevasCampañas = e.target.checked
                                              ? [...formularioObjetivo.campanas_relacionadas, campaña.id]
                                              : formularioObjetivo.campanas_relacionadas.filter(id => id !== campaña.id);
                                            setFormularioObjetivo({
                                              ...formularioObjetivo,
                                              campanas_relacionadas: nuevasCampañas
                                            });
                                          }}
                                          className="mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-white/20 rounded"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <div className="text-xs text-white/80 truncate">{campaña.nombre}</div>
                                          <div className="flex items-center space-x-1.5 mt-0.5">
                                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                                              campaña.status === 'ACTIVE' || campaña.status === 'ENABLED'
                                                ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                                : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                            }`}>
                                              {campaña.status}
                                            </span>
                                            <span className="px-1.5 py-0.5 bg-white/10 text-white/60 rounded text-xs">
                                              {campaña.tipo}
                                            </span>
                                          </div>
                                        </div>
                                      </label>
                                    ))
                                  )}
                                </div>
                              </div>
                              
                              {/* Columna 2: Campañas seleccionadas */}
                              <div className="flex flex-col">
                                <h4 className="text-xs font-medium text-white/80 mb-1.5 flex items-center">
                                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5"></span>
                                  Campañas Seleccionadas ({formularioObjetivo.campanas_relacionadas.length})
                                </h4>
                                {/* Espacio para alinear con el buscador de la columna izquierda */}
                                <div className="mb-2 h-[38px]"></div>
                                <div className="flex-1 h-96 overflow-y-auto border border-white/20 rounded p-2 space-y-1.5 bg-white/5">
                                  {formularioObjetivo.campanas_relacionadas.length === 0 ? (
                                    <div className="text-center py-8">
                                      <p className="text-xs text-white/50">No hay campañas seleccionadas</p>
                                    </div>
                                  ) : (
                                    (formularioObjetivo.campanas_relacionadas || []).map(campañaId => {
                                      const campaña = campañasFiltradas.find(c => c.id === campañaId);
                                      return campaña ? (
                                        <div key={campañaId} className="flex items-center justify-between p-1.5 bg-green-500/10 rounded border border-green-500/20">
                                          <div className="flex-1 min-w-0">
                                            <div className="text-xs text-white/80 truncate">{campaña.nombre}</div>
                                            <div className="flex items-center space-x-1.5 mt-0.5">
                                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                                campaña.status === 'ACTIVE' || campaña.status === 'ENABLED'
                                                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                                  : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                              }`}>
                                                {campaña.status}
                                              </span>
                                            </div>
                                          </div>
                                          <button
                                            onClick={() => {
                                              const nuevasCampañas = formularioObjetivo.campanas_relacionadas.filter(id => id !== campañaId);
                                              setFormularioObjetivo({
                                                ...formularioObjetivo,
                                                campanas_relacionadas: nuevasCampañas
                                              });
                                            }}
                                            className="ml-2 p-0.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                                          >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                          </button>
                                        </div>
                                      ) : null;
                                    })
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Paso 4: Confirmación final */}
                {pasoActual === 4 && (
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">Confirmá tu objetivo</h4>
                    </div>

                    {/* Nombre del objetivo autogenerado */}
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-white/90 mb-1">
                          Nombre del Objetivo (autogenerado, editable)
                        </label>
                        <input
                          type="text"
                          value={formularioObjetivo.nombre}
                          onChange={(e) =>
                            setFormularioObjetivo({
                              ...formularioObjetivo,
                              nombre: e.target.value,
                            })
                          }
                          className="w-full px-2 py-1.5 text-xs border border-white/20 rounded bg-white/5 text-white/95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-1 focus-visible:ring-offset-slate-900"
                        />
                      </div>

                      {/* Resumen visual */}
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/20">
                        <div className="bg-white/5 border border-white/10 rounded p-2">
                          <p className="text-xs text-white/60 mb-0.5">Métrica</p>
                          <p className="text-xs font-semibold text-white">{formularioObjetivo.metrica_relacionada}</p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded p-2">
                          <p className="text-xs text-white/60 mb-0.5">Target</p>
                          <p className="text-xs font-semibold text-white">{formularioObjetivo.target || 'N/A'}</p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded p-2">
                          <p className="text-xs text-white/60 mb-0.5">Plataforma</p>
                          <p className="text-xs font-semibold text-white">{formularioObjetivo.plataformas_relacionadas[0] || 'N/A'}</p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded p-2">
                          <p className="text-xs text-white/60 mb-0.5">Campañas</p>
                          <p className="text-xs font-semibold text-white">{formularioObjetivo.campanas_relacionadas.length}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Botones de navegación */}
              <div className="p-3 sm:p-4 border-t border-white/20 flex justify-between items-center">
                <button
                  onClick={() => {
                    if (pasoActual > 1) {
                      // Si estamos en el paso 3 y no requiere conversiones, volver al paso 1
                      if (pasoActual === 3 && !mostrarSelectorConversiones) {
                        setPasoActual(1);
                      } else {
                        setPasoActual(pasoActual - 1);
                      }
                    } else {
                      cerrarModal();
                    }
                  }}
                  className="px-3 sm:px-4 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200 font-medium text-xs sm:text-sm backdrop-blur-md flex items-center space-x-2"
                >
                  <ArrowLeft size={14} />
                  <span>{pasoActual === 1 ? 'Cancelar' : 'Anterior'}</span>
                </button>
                
                {pasoActual < 4 ? (
                  <button
                    onClick={() => {
                      // Validaciones básicas antes de avanzar
                      if (pasoActual === 1) {
                        if (!formularioObjetivo.metrica_relacionada) {
                          setError('Por favor selecciona una métrica');
                          return;
                        }
                        if (!formularioObjetivo.target || formularioObjetivo.target <= 0) {
                          setError('Por favor ingresa un target válido');
                          return;
                        }
                        if (!formularioObjetivo.fecha_inicio || !formularioObjetivo.fecha_fin) {
                          setError('Por favor selecciona un rango de fechas');
                          return;
                        }
                        if (!formularioObjetivo.plataformas_relacionadas || formularioObjetivo.plataformas_relacionadas.length === 0) {
                          setError('Por favor selecciona una plataforma');
                          return;
                        }
                        // Si requiere conversiones, avanzar al paso 2, sino al paso 3
                        if (mostrarSelectorConversiones) {
                          setError(null);
                          setPasoActual(2);
                        } else {
                          setError(null);
                          setPasoActual(3);
                        }
                        return;
                      } else if (pasoActual === 2) {
                        // Validar conversiones en paso 2
                        if (conversionesSeleccionadas.length === 0) {
                          setError('Por favor selecciona al menos una conversión');
                          return;
                        }
                        setError(null);
                        setPasoActual(3);
                        return;
                      } else if (pasoActual === 3) {
                        // Validar campañas en paso 3
                        if (formularioObjetivo.campanas_relacionadas.length === 0) {
                          setError('Por favor selecciona al menos una campaña');
                          return;
                        }
                        setError(null);
                        setPasoActual(4);
                        return;
                      }
                    }}
                    className="px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl font-medium flex items-center justify-center space-x-2 text-xs sm:text-sm backdrop-blur-md"
                  >
                    <span>Siguiente</span>
                    <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    onClick={editandoObjetivo ? handleActualizarObjetivo : handleCrearObjetivo}
                    disabled={submittingObjetivo}
                    className={`px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg transition-all duration-300 shadow-lg font-medium flex items-center justify-center space-x-2 text-xs sm:text-sm backdrop-blur-md ${
                      submittingObjetivo
                        ? 'opacity-60 cursor-not-allowed'
                        : 'hover:from-blue-700 hover:to-purple-700 hover:shadow-xl'
                    }`}
                  >
                    {submittingObjetivo ? (
                      <>
                        <span
                          aria-hidden="true"
                          className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"
                        />
                        <span>Procesando...</span>
                      </>
                    ) : (
                      <>
                        <Target size={14} />
                        <span>{editandoObjetivo ? 'Actualizar Objetivo' : 'Crear Objetivo'}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contenido del módulo */}
        <div className="w-full px-2 sm:px-4 mb-8 mt-8">
          {(loading || esperandoCuentas || loadingOptimizations) ? (
            <div className="flex flex-col items-center justify-center py-12 text-white/70">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
              <p>{loadingOptimizations ? 'Cargando optimizaciones...' : 'Cargando objetivos...'}</p>
            </div>
          ) : !brandActivo ? (
            <div className="text-center py-8">
              <Building2 size={64} className="mx-auto text-white/30 mb-4" />
              <h3 className="text-xl font-semibold text-white/80 mb-2">No hay marcas disponibles</h3>
              <p className="text-white/60">Selecciona una marca desde el sidebar para ver sus objetivos</p>
            </div>
          ) : (objetivos || []).length > 0 ? (
            <div className="mt-2">
              <div className="flex flex-col items-center gap-1 mb-5">
                <button
                  type="button"
                  onClick={handleActualizarValoresActuales}
                  disabled={!brandActivo || loading || refreshingMetricasActuales}
                  className={`inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-white/20 bg-white/5 text-white/90 text-sm font-medium backdrop-blur-sm shadow-md transition ${
                    !brandActivo || loading || refreshingMetricasActuales
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-white/10 hover:border-white/30'
                  }`}
                  title="Sincronizar valores actuales con tus plataformas"
                  aria-label="Actualizar valores actuales de los objetivos"
                >
                  <RefreshCw
                    className={`w-4 h-4 mr-2 shrink-0 ${refreshingMetricasActuales ? 'animate-spin' : ''}`}
                    aria-hidden
                  />
                  Actualizar valores actuales
                </button>
                <p className="text-white/45 text-xs text-center max-w-md">
                  Actualiza el progreso de cada objetivo con los datos más recientes.
                </p>
              </div>
              {renderTablaActivos()}
              {renderTablaProximos()}
              {renderTablaFinalizados()}
            </div>
          ) : null}
          <div className="mt-6">
            <p className="text-white/90 text-sm font-semibold text-center mb-1">
              Crear objetivo por plataforma
            </p>
            <p className="text-white/50 text-xs text-center mb-4">
              Seleccioná la plataforma y el método de creación
            </p>
            <div className="grid grid-cols-1 gap-3 mx-auto md:grid-cols-2 max-w-3xl">
              {platformCards.map((platform) => (
                <div key={platform.value} className="rounded-xl bg-white/5 border border-white/10 p-3 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    {platform.value === 'GOOGLE' ? <GoogleIcon width={20} height={20} /> : <MetaIcon width={20} height={20} />}
                    <span className="text-white font-medium text-sm">{platform.label}</span>
                  </div>
                  {platform.isConnected ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openCreateObjectivesSetupModal({ platform: platform.value })}
                        className="flex items-start gap-2 w-full text-left p-2.5 rounded-lg border border-white/10 hover:border-fuchsia-400/30 hover:bg-white/5 transition"
                      >
                        <span className="rounded-full border-2 border-white/40 w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Sparkles className="w-3.5 h-3.5 text-fuchsia-400 flex-shrink-0" />
                            <span className="text-white text-xs font-medium">Con IA</span>
                            <span className="px-1 py-0.5 rounded text-[9px] font-semibold bg-fuchsia-500/30 text-fuchsia-200 border border-fuchsia-400/40">RECOMENDADO</span>
                          </div>
                          <p className="text-white/50 text-[11px] mt-0.5">Genera objetivos optimizados con IA basados en tus datos.</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={abrirModalCrear}
                        className="flex items-start gap-2 w-full text-left p-2.5 rounded-lg border border-white/10 hover:border-white/20 hover:bg-white/5 transition"
                      >
                        <span className="rounded-full border-2 border-white/40 w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Pencil className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
                            <span className="text-white text-xs font-medium">Manual</span>
                          </div>
                          <p className="text-white/50 text-[11px] mt-0.5">Definí tus objetivos con total control sobre cada métrica.</p>
                        </div>
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-white/50 text-xs">Conectá tu cuenta para crear y gestionar objetivos en esta plataforma.</p>
                      <button
                        type="button"
                        onClick={() => platform.value === 'GOOGLE' ? connectGoogleAds(false) : startMetaOAuth(false)}
                        className="w-full px-3 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium hover:from-blue-700 hover:to-purple-700 transition shadow-lg hover:shadow-xl"
                      >
                        <span className="flex items-center justify-center gap-2">
                          {platform.value === 'GOOGLE' ? <GoogleIcon width={18} height={18} /> : <MetaIcon width={18} height={18} />}
                          Conectá {platform.label}
                        </span>
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal para crear objetivos con IA (mismo flujo que onboarding) */}
      <CreateObjectivesModal
        isOpen={isCreateObjectivesAIModalOpen}
        onClose={() => {
          setIsCreateObjectivesAIModalOpen(false);
          // Si se cerró con backdrop/X, igualmente refrescar por si ya se generaron objetivos.
          void cargarObjetivos();
        }}
        onSuccess={async () => {
          // Recargar objetivos para reflejar los recién generados
          await cargarObjetivos();
        }}
      />

      {/* Modal de listado de optimizaciones */}
      <OptimizationsListModal
        isOpen={isOptimizationsListModalOpen}
        onClose={() => {
          setGlobalOptimizationsListModalOpen(false);
          setIsOptimizationsListModalOpen(false);
          setSelectedObjectiveId(null);
          setSelectedObjectiveName(null);
        }}
        groups={selectedObjectiveId ? (objectiveGroupsByObjective[selectedObjectiveId] || []) : []}
        getObjectiveName={getObjectiveName}
        onSelectOptimization={handleSelectOptimization}
        objectiveName={selectedObjectiveName}
        variant="cards"
      />

      {/* Modal de detalle de optimización */}
      <OptimizationDetailModal
        isOpen={isOptimizationModalOpen}
        onClose={() => {
          setGlobalOptimizationDetailModalOpen(false);
          setIsOptimizationModalOpen(false);
          setSelectedOptimization(null);
          setSelectedObjectiveId(null);
          setCurrentOptimizationIndex(0);
        }}
        optimization={selectedOptimization}
        allOptimizations={selectedObjectiveId ? getOptimizationCardsForObjective(selectedObjectiveId) : []}
        currentIndex={currentOptimizationIndex}
        onNavigate={handleNavigateOptimization}
        onRatingSuccess={handleRatingSuccess}
      />
    </div>
  );
};

export default Objetivos;
